import { useState, useEffect, useCallback, useMemo, useRef, Fragment } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../api/client";
import Navbar from "../components/layout/Navbar";
import MobileNavbar from "../components/layout/MobileNavbar";
import TeamPersonnelGroup from "../components/common/TeamPersonnelGroup";
import NapFieldsGroup, { napFieldsComplete, vacantPortsPayload } from "../components/common/NapFieldsGroup";
import NapCodeMapLinks from "../components/common/NapCodeMapLinks";
import ButtonSpinner from "../components/common/ButtonSpinner";
import FetchingOverlay from "../components/common/FetchingOverlay";
import MigrationImportModal from "../components/modals/MigrationImportModal";
import MigrationDetailModal from "../components/modals/MigrationDetailModal";
import MigrationChatModal from "../components/modals/MigrationChatModal";
import StatusActionMenu from "../components/StatusActionMenu";
import CallStatusActionMenu from "../components/CallStatusActionMenu";
import TeamAssignmentPopover from "../components/common/TeamAssignmentPopover";
import BulkTeamAssignModal from "../components/common/BulkTeamAssignModal";
import DateFilter from "../components/dashboard/DateFilter";
import useAuth from "../hooks/useAuth";
import useIsMobile from "../hooks/useIsMobile";
import useChatNotifications from "../hooks/useChatNotifications";
import useCompositionGuard from "../hooks/useCompositionGuard";
import { useConfirm } from "../context/ConfirmContext";
import { extractErrorMessage } from "../utils/helpers";
import { getLocalToday, getLocalTomorrow, calculatePendingAgeing, getAgeingColor } from "../utils/helpers";
import { getJoStatusColor } from "../utils/joStatusColors";
import { getCallStatusColor } from "../utils/callStatusOptions";
import { MODULE_THEMES } from "../utils/moduleTheme";
import MigrationMobileMap from "../components/mobile/MigrationMobileMap";
import { geocodeSubscriberAddress } from "../services/geocodingService";

const MIG = MODULE_THEMES.migration;

/** Normalize a date string to YYYY-MM-DD for <input type="date">. */
function toInputDate(val) {
  if (!val) return "";
  const d = new Date(val);
  if (isNaN(d)) return val;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/** Format any date-like string to MM-DD-YYYY (no time). */
function fmtDate(val) {
  if (!val) return "—";
  const d = new Date(val);
  if (isNaN(d)) return val;
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${mm}-${dd}-${yyyy}`;
}

/** Format call status for display — "Others" becomes "Other - <custom text>". */
function fmtCallStatus(r) {
  if (!r.call_status) return r.call_status;
  if (r.call_status === "Others" && r.call_status_other_text?.trim()) {
    return `Other - ${r.call_status_other_text.trim()}`;
  }
  return r.call_status;
}

/** Strip labels like HOME:, CELL:, CEL1: from contact info, keep only numbers. */
function cleanContact(val) {
  if (!val) return "";
  return val.replace(/\b(HOME|CELL|CEL1|TEL|FAX|MOBILE|MOB)\s*:\s*/gi, "").replace(/;\s*$/, "").trim();
}

const STAGE_LABELS = {
  uploaded_for_migration: "Uploaded for Migration",
  for_dispatch: "For Dispatch",
  visited_called: "Completed/Visited/Called",
  for_calling_scheduling: "For Calling/Scheduling",
};

const TAG_TARGETS = [
  { stage: "for_dispatch", label: "For Dispatch", color: "bg-indigo-600 hover:bg-indigo-700" },
  { stage: "visited_called", label: "Completed/Visited/Called", color: "bg-teal-600 hover:bg-teal-700" },
  { stage: "for_calling_scheduling", label: "For Calling/Scheduling", color: "bg-orange-500 hover:bg-orange-600" },
];

const CALLING_TAG_TARGETS = [
  { stage: "for_dispatch", label: "For Dispatch", color: "bg-indigo-600 hover:bg-indigo-700" },
  { stage: "visited_called", label: "Completed/Visited/Called", color: "bg-teal-600 hover:bg-teal-700" },
];

// Columns shown in main table — varies by stage
function getColumns(stage) {
  const cols = [];

  // Date column varies by stage
  if (stage === "for_dispatch" || stage === "visited_called" || stage === "uploaded_for_migration") {
    cols.push({ key: "dispatch_date", label: "Dispatch Date", hideable: true });
  } else if (stage === "for_calling_scheduling") {
    cols.push({ key: "endorsed_date", label: "Date Endorsed", hideable: true });
  }

  // Team Number, Personnel, JO Status, JO Remarks, JO Number — hidden on For Calling page
  if (stage !== "for_calling_scheduling") {
    cols.push(
      { key: "team_number", label: "Team Number", hideable: false },
      { key: "personnel", label: "Personnel", hideable: false },
      { key: "jo_status", label: "JO Status", hideable: false },
      { key: "remarks", label: "JO Remarks", hideable: true },
      { key: "nap_code", label: "NAP Code", hideable: true, defaultHidden: true },
      { key: "jo_number", label: "JO Number", hideable: true },
    );
  }

  // Call Status + Call Remarks on calling, dispatch, visited pages
  if (stage === "for_calling_scheduling" || stage === "for_dispatch" || stage === "visited_called") {
    cols.push(
      { key: "call_status", label: "Call Status", hideable: false },
      { key: "call_remarks", label: "Call Remarks", hideable: true },
    );
  }

  // Call Attempts column — only on For Calling/Scheduling page
  if (stage === "for_calling_scheduling") {
    cols.push({ key: "call_attempt_count", label: "Call Attempts", hideable: true });
  }

  cols.push(
    { key: "oldacctno", label: "Old Account No.", hideable: false },
    { key: "new_account_number", label: "New Account No.", hideable: true, defaultHidden: true },
    { key: "bundletype", label: "Bundle Type", hideable: true },
  );

  // SDU/MDU — sourced from Subscriber Information; shown on the For Dispatch page
  if (stage === "for_dispatch") {
    cols.push({ key: "sdu_mdu", label: "SDU/MDU", hideable: true });
  }

  cols.push(
    { key: "name", label: "Name", hideable: false },
    { key: "decomm", label: "Decomm", hideable: true },
    { key: "municipalityname", label: "Municipality", hideable: true },
    { key: "brgy", label: "Barangay", hideable: true },
    { key: "address", label: "Address", hideable: true },
    { key: "contact_info", label: "Contact Info", hideable: true },
    { key: "updated_by_name", label: "Updated By", hideable: true },
  );

  return cols;
}

// (SortIcon is rendered inline using sortStack below)

/* ─── Migration Cancellation / Reschedule reason options ─── */
const MIG_CANCELLATION_REASONS = [
  "Cancelled by Subs", "Needs RTA", "Full NAP", "No Converge Facility", "For NI", "Other reason",
];
const MIG_RESCHEDULE_REASONS = [
  "Needs permit", "Requested by customer", "Subs not around", "Other reason",
];

function localToday() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/* ─── Self-contained Complete dialog (migration) ─── */
function MigQuickCompleteDialog({ record, onDone, onCancel }) {
  const { user } = useAuth();
  const { confirm } = useConfirm();
  const [teamNumber, setTeamNumber] = useState(record?.team_number || "");
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [personnel1Id, setPersonnel1Id] = useState(record?.personnel_1_id || null);
  const [personnel2Id, setPersonnel2Id] = useState(record?.personnel_2_id || null);
  const [joNumber, setJoNumber] = useState(record?.jo_number || "");
  const [newAccountNumber, setNewAccountNumber] = useState(record?.new_account_number || "");
  const [remark, setRemark] = useState(record?.remarks || "");
  const [serialNumber, setSerialNumber] = useState(record?.modem_serial_number || "");
  const [recordIdField, setRecordIdField] = useState(record?.iptv_serial_number || "");
  const [napCode, setNapCode] = useState(record?.nap_code || "");
  const [napId, setNapId] = useState(record?.nap_id || "");
  const [napPort, setNapPort] = useState(record?.nap_port || "");
  const [vacantPorts, setVacantPorts] = useState(record?.vacant_ports ?? "");
  const [napPhoto, setNapPhoto] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const { check: checkComp, ConflictDialog } = useCompositionGuard();

  // Match team on mount
  useState(() => {
    if (record?.team_number) {
      api.get("/team-masters/active", { params: { company_id: user?.company_id } })
        .then(({ data }) => {
          const match = data.find((t) => t.team_number === record.team_number);
          if (match) setSelectedTeamId(match.id);
        }).catch(() => {});
    }
  });

  const canConfirm =
    teamNumber.trim() && personnel1Id && joNumber.trim() && newAccountNumber.trim() && remark.trim() && serialNumber.trim() &&
    napFieldsComplete({ napCode, napId, napPort, vacantPorts }) && !!napPhoto;

  const handleConfirm = async () => {
    if (!canConfirm) return;
    const ok = await checkComp({ teamNumber, assignmentDate: record?.dispatch_date, personnel1Id, personnel2Id });
    if (!ok) return;
    setSubmitting(true); setError("");
    try {
      const photoForm = new FormData();
      photoForm.append("file", napPhoto);
      await api.post(`/migration/${record.id}/nap-photo`, photoForm);
      const { data } = await api.post(`/migration/${record.id}/complete`, { team_number: teamNumber, personnel_1_id: personnel1Id, personnel_2_id: personnel2Id, jo_number: joNumber, new_account_number: newAccountNumber, remarks: remark, modem_serial_number: serialNumber, iptv_serial_number: recordIdField || null, nap_code: napCode, nap_id: napId, nap_port: napPort, vacant_ports: vacantPortsPayload(vacantPorts) });
      setSubmitting(false);
      await confirm({
        title: "Migration Completed",
        message: data?.nap_details_updated
          ? "Migration record completed successfully and NAP details updated."
          : "Migration record completed successfully.",
        confirmLabel: "OK",
        cancelLabel: "OK",
      });
      onDone();
    } catch (err) { setError(extractErrorMessage(err, "Failed to complete.")); setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
      <FetchingOverlay show={submitting} />
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold mb-2">Complete Migration Record</h3>
        <p className="text-sm text-gray-600 mb-3">Are you sure you want to mark this record as Completed?</p>
        <div className="bg-gray-50 border rounded-lg px-3 py-2.5 mb-4 space-y-1">
          <p className="text-sm font-semibold text-gray-900 truncate" title={record?.name}>{record?.name || "—"}</p>
          <p className="text-xs text-gray-500">{record?.address || "—"}</p>
          <p className="text-xs text-gray-500"><span className="font-medium text-gray-600">Account Number:</span> {record?.oldacctno || "—"}</p>
        </div>
        <TeamPersonnelGroup
          teamMasterId={selectedTeamId}
          personnel1Id={personnel1Id}
          personnel2Id={personnel2Id}
          onTeamChange={(teamId, teamData) => {
            setSelectedTeamId(teamId);
            if (teamData) setTeamNumber(teamData.team_number);
            else setTeamNumber("");
          }}
          onPersonnel1Change={(id) => setPersonnel1Id(id)}
          onPersonnel2Change={(id) => setPersonnel2Id(id)}
          companyId={user?.company_id}
          assignmentDate={record?.dispatch_date}
        />
        <label className="block text-sm font-medium mb-1 mt-3">JO Number <span className="text-red-500">*</span></label>
        <input type="text" value={joNumber} onChange={(e) => setJoNumber(e.target.value)} placeholder="Enter JO Number..." className="w-full border rounded px-3 py-2 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-blue-500" />
        <label className="block text-sm font-medium mb-1">New Account Number <span className="text-red-500">*</span></label>
        <input type="text" value={newAccountNumber} onChange={(e) => setNewAccountNumber(e.target.value)} placeholder="Enter New Account Number..." className="w-full border rounded px-3 py-2 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-blue-500" />
        <label className="block text-sm font-medium mb-1">JO Status Remarks <span className="text-red-500">*</span></label>
        <textarea value={remark} onChange={(e) => setRemark(e.target.value)} rows={3} placeholder="Enter JO Status Remarks..." className="w-full border rounded px-3 py-2 text-sm mb-3 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500" />
        <NapFieldsGroup
          napCode={napCode} onNapCodeChange={setNapCode}
          napId={napId} onNapIdChange={setNapId}
          napPort={napPort} onNapPortChange={setNapPort}
          vacantPorts={vacantPorts} onVacantPortsChange={setVacantPorts}
          showPhoto napPhoto={napPhoto} onNapPhotoChange={setNapPhoto}
        />
        <label className="block text-sm font-medium mb-1">Modem Serial Number <span className="text-red-500">*</span></label>
        <input type="text" value={serialNumber} onChange={(e) => setSerialNumber(e.target.value)} placeholder="Enter Modem Serial Number..." className="w-full border rounded px-3 py-2 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-blue-500" />
        <label className="block text-sm font-medium mb-1">IPTV Serial Number</label>
        <input type="text" value={recordIdField} onChange={(e) => setRecordIdField(e.target.value)} placeholder="Enter IPTV Serial Number..." className="w-full border rounded px-3 py-2 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500" />
        {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onCancel} className="px-4 py-2 text-sm border rounded hover:bg-gray-100">Cancel</button>
          <button type="button" disabled={!canConfirm || submitting} onClick={handleConfirm} className="px-4 py-2 text-sm text-white rounded disabled:opacity-50 bg-green-600 hover:bg-green-700 inline-flex items-center gap-2">{submitting && <ButtonSpinner />}{submitting ? "Completing…" : "Confirm"}</button>
        </div>
      </div>
    </div>
  );
}

/* ─── Self-contained Reschedule dialog (migration) ─── */
function MigQuickRescheduleDialog({ record, onDone, onCancel }) {
  const { user } = useAuth();
  const [teamNumber, setTeamNumber] = useState(record?.team_number || "");
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [personnel1Id, setPersonnel1Id] = useState(record?.personnel_1_id || null);
  const [personnel2Id, setPersonnel2Id] = useState(record?.personnel_2_id || null);
  const [remark, setRemark] = useState("");
  const [reasons, setReasons] = useState([]);
  const { check: checkComp, ConflictDialog } = useCompositionGuard();

  // Match team on mount
  useState(() => {
    if (record?.team_number) {
      api.get("/team-masters/active", { params: { company_id: user?.company_id } })
        .then(({ data }) => {
          const match = data.find((t) => t.team_number === record.team_number);
          if (match) setSelectedTeamId(match.id);
        }).catch(() => {});
    }
  });
  const [newDate, setNewDate] = useState(() => { const d = new Date(); d.setDate(d.getDate() + 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const toggleReason = (r) => setReasons((prev) => prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]);

  const buildFinalRemark = () => {
    const prefix = reasons.length > 0 ? reasons.join("; ") : "";
    const text = remark.trim();
    if (prefix && text) return `${prefix} - ${text}`;
    return text || "";
  };

  const minDate = (() => {
    const t = localToday();
    const [y, m, d] = t.split("-").map(Number);
    const nd = new Date(y, m - 1, d + 1);
    return `${nd.getFullYear()}-${String(nd.getMonth() + 1).padStart(2, "0")}-${String(nd.getDate()).padStart(2, "0")}`;
  })();

  const canConfirm = remark.trim() && newDate && reasons.length > 0;

  const handleConfirm = async () => {
    if (!canConfirm) return;
    const ok = await checkComp({ teamNumber, assignmentDate: newDate, personnel1Id, personnel2Id });
    if (!ok) return;
    setSubmitting(true); setError("");
    try {
      await api.post(`/migration/${record.id}/reschedule`, { team_number: teamNumber || null, personnel_1_id: personnel1Id, personnel_2_id: personnel2Id, remarks: buildFinalRemark(), new_dispatch_date: newDate, reschedule_reasons: reasons });
      onDone();
    } catch (err) { setError(extractErrorMessage(err, "Failed to reschedule.")); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold mb-2">Reschedule Migration Record</h3>
        <p className="text-sm text-gray-600 mb-3">Are you sure you want to reschedule this record?</p>
        <div className="bg-gray-50 border rounded-lg px-3 py-2.5 mb-4 space-y-1">
          <p className="text-sm font-semibold text-gray-900 truncate" title={record?.name}>{record?.name || "—"}</p>
          <p className="text-xs text-gray-500">{record?.address || "—"}</p>
          <p className="text-xs text-gray-500"><span className="font-medium text-gray-600">Account Number:</span> {record?.oldacctno || "—"}</p>
        </div>
        <TeamPersonnelGroup
          teamMasterId={selectedTeamId}
          personnel1Id={personnel1Id}
          personnel2Id={personnel2Id}
          onTeamChange={(teamId, teamData) => {
            setSelectedTeamId(teamId);
            if (teamData) setTeamNumber(teamData.team_number);
            else setTeamNumber("");
          }}
          onPersonnel1Change={(id) => setPersonnel1Id(id)}
          onPersonnel2Change={(id) => setPersonnel2Id(id)}
          companyId={user?.company_id}
          assignmentDate={record?.dispatch_date}
        />
        <label className="block text-sm font-medium mb-1">JO Status Remarks <span className="text-red-500">*</span></label>
        <textarea value={remark} onChange={(e) => setRemark(e.target.value)} rows={3} placeholder="Enter JO Status Remarks..." className="w-full border rounded px-3 py-2 text-sm mb-3 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500" />
        <label className="block text-sm font-medium mb-1">New Dispatch Date <span className="text-red-500">*</span></label>
        <input type="date" min={minDate} value={newDate} onChange={(e) => setNewDate(e.target.value)} className="w-full border rounded px-3 py-2 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-blue-500" />
        <label className="block text-sm font-medium mb-1">Reschedule Reasons <span className="text-red-500">*</span></label>
        <div className="space-y-1.5 mb-1">
          {MIG_RESCHEDULE_REASONS.map((r) => (
            <label key={r} className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
              <input type="checkbox" checked={reasons.includes(r)} onChange={() => toggleReason(r)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
              {r}
            </label>
          ))}
        </div>
        {reasons.length > 0 && remark.trim() && (
          <p className="text-xs text-gray-500 mt-2">Will save as: <span className="font-medium">{buildFinalRemark()}</span></p>
        )}
        {!canConfirm && (
          <p className="text-xs text-red-500 mt-2">
            {!remark.trim() && reasons.length === 0 ? "Please fill in all required fields." : !remark.trim() ? "Please enter JO Status Remarks." : reasons.length === 0 ? "Please select at least one reschedule reason." : "Please select a new dispatch date."}
          </p>
        )}
        {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
        <div className="flex justify-end gap-2 mt-4">
          <button type="button" onClick={onCancel} className="px-4 py-2 text-sm border rounded hover:bg-gray-100">Cancel</button>
          <button type="button" disabled={!canConfirm || submitting} onClick={handleConfirm} className="px-4 py-2 text-sm text-white rounded disabled:opacity-50 bg-blue-600 hover:bg-blue-700">{submitting ? "Rescheduling…" : "Reschedule"}</button>
        </div>
      </div>
      <ConflictDialog />
    </div>
  );
}

/* ─── Self-contained Cancel dialog (migration) ─── */
function MigQuickCancelDialog({ record, onDone, onCancel }) {
  const { user } = useAuth();
  const [teamNumber, setTeamNumber] = useState(record?.team_number || "");
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [personnel1Id, setPersonnel1Id] = useState(record?.personnel_1_id || null);
  const [personnel2Id, setPersonnel2Id] = useState(record?.personnel_2_id || null);
  const [remark, setRemark] = useState("");
  const [reasons, setReasons] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const { check: checkComp, ConflictDialog } = useCompositionGuard();

  // Match team on mount
  useState(() => {
    if (record?.team_number) {
      api.get("/team-masters/active", { params: { company_id: user?.company_id } })
        .then(({ data }) => {
          const match = data.find((t) => t.team_number === record.team_number);
          if (match) setSelectedTeamId(match.id);
        }).catch(() => {});
    }
  });

  const toggleReason = (r) => setReasons((prev) => prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]);

  const buildFinalRemark = () => {
    const prefix = reasons.length > 0 ? reasons.join("; ") : "";
    const text = remark.trim();
    if (prefix && text) return `${prefix} - ${text}`;
    return text || "";
  };

  const canConfirm = remark.trim() && reasons.length > 0;

  const handleConfirm = async () => {
    if (!canConfirm) return;
    const ok = await checkComp({ teamNumber, assignmentDate: record?.dispatch_date, personnel1Id, personnel2Id });
    if (!ok) return;
    setSubmitting(true); setError("");
    try {
      await api.post(`/migration/${record.id}/cancel`, { team_number: teamNumber || null, personnel_1_id: personnel1Id, personnel_2_id: personnel2Id, remarks: buildFinalRemark(), cancellation_reasons: reasons });
      onDone();
    } catch (err) { setError(extractErrorMessage(err, "Failed to cancel.")); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold mb-2">Cancel Migration Record</h3>
        <p className="text-sm text-gray-600 mb-3">Are you sure you want to cancel this record?</p>
        <div className="bg-gray-50 border rounded-lg px-3 py-2.5 mb-4 space-y-1">
          <p className="text-sm font-semibold text-gray-900 truncate" title={record?.name}>{record?.name || "—"}</p>
          <p className="text-xs text-gray-500">{record?.address || "—"}</p>
          <p className="text-xs text-gray-500"><span className="font-medium text-gray-600">Account Number:</span> {record?.oldacctno || "—"}</p>
        </div>
        <TeamPersonnelGroup
          teamMasterId={selectedTeamId}
          personnel1Id={personnel1Id}
          personnel2Id={personnel2Id}
          onTeamChange={(teamId, teamData) => {
            setSelectedTeamId(teamId);
            if (teamData) setTeamNumber(teamData.team_number);
            else setTeamNumber("");
          }}
          onPersonnel1Change={(id) => setPersonnel1Id(id)}
          onPersonnel2Change={(id) => setPersonnel2Id(id)}
          companyId={user?.company_id}
          assignmentDate={record?.dispatch_date}
        />
        <label className="block text-sm font-medium mb-1">JO Status Remarks <span className="text-red-500">*</span></label>
        <textarea value={remark} onChange={(e) => setRemark(e.target.value)} rows={3} placeholder="Enter JO Status Remarks..." className="w-full border rounded px-3 py-2 text-sm mb-3 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500" />
        <label className="block text-sm font-medium mb-1">Cancellation Reasons <span className="text-red-500">*</span></label>
        <div className="space-y-1.5 mb-1">
          {MIG_CANCELLATION_REASONS.map((r) => (
            <label key={r} className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
              <input type="checkbox" checked={reasons.includes(r)} onChange={() => toggleReason(r)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
              {r}
            </label>
          ))}
        </div>
        {reasons.length > 0 && remark.trim() && (
          <p className="text-xs text-gray-500 mt-2">Will save as: <span className="font-medium">{buildFinalRemark()}</span></p>
        )}
        {!canConfirm && (
          <p className="text-xs text-red-500 mt-2">
            {reasons.length === 0 && !remark.trim() ? "Please select at least one cancellation reason and enter a remark." : reasons.length === 0 ? "Please select at least one cancellation reason." : "Please enter a JO Status Remark."}
          </p>
        )}
        {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
        <div className="flex justify-end gap-2 mt-4">
          <button type="button" onClick={onCancel} className="px-4 py-2 text-sm border rounded hover:bg-gray-100">Cancel</button>
          <button type="button" disabled={!canConfirm || submitting} onClick={handleConfirm} className="px-4 py-2 text-sm text-white rounded disabled:opacity-50 bg-red-600 hover:bg-red-700">{submitting ? "Cancelling…" : "Confirm"}</button>
        </div>
      </div>
      <ConflictDialog />
    </div>
  );
}

/* Migration call status options for quick action menu (For Calling/Scheduling) */
const MIG_CALL_STATUS_OPTIONS = [
  "Confirmed", "KOR", "CBR", "Cancelled", "Unattended", "Busy tone", "Others"
];
const DISPATCH_REQUIRED_STATUSES = new Set(["Confirmed"]);

const MIG_CALL_CANCEL_REASONS = [
  "Cancelled by Subs",
  "Full NAP",
  "For New Installation",
  "Already Migrated",
  "Other reason",
];

/* ─── Self-contained Quick Call Status dialog (migration — for_calling_scheduling only) ─── */
function MigQuickCallStatusDialog({ record, selectedStatus, onDone, onCancel }) {
  const isOthers = selectedStatus === "Others";
  const isCancelled = selectedStatus === "Cancelled";
  const needsDispatchDate = DISPATCH_REQUIRED_STATUSES.has(selectedStatus);
  const [customText, setCustomText] = useState("");
  const [dispatchDate, setDispatchDate] = useState(needsDispatchDate ? getLocalTomorrow() : "");
  const [remarks, setRemarks] = useState("");
  const [cancelReasons, setCancelReasons] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const toggleReason = (reason) =>
    setCancelReasons((prev) => prev.includes(reason) ? prev.filter((r) => r !== reason) : [...prev, reason]);

  const canConfirm = remarks.trim()
    && (!isOthers || customText.trim())
    && (!needsDispatchDate || dispatchDate)
    && (!isCancelled || cancelReasons.length > 0);

  const handleConfirm = async () => {
    if (!canConfirm) return;
    setSubmitting(true);
    setError("");
    try {
      const payload = {
        call_status: selectedStatus,
        call_remarks: isCancelled
          ? `${cancelReasons.join("; ")} - ${remarks.trim()}`
          : remarks.trim(),
      };
      if (isOthers) payload.call_status_other_text = customText.trim();
      if (needsDispatchDate) {
        payload.dispatch_date = dispatchDate;
        // Geocode the subscriber's address using the same lookup performed when
        // clicking the subscriber name on the dispatch map, so the record moves
        // to For Dispatch with a precise pin instead of the barangay centroid.
        // Best-effort: a failed/empty geocode must not block the stage move.
        try {
          let fullAddress = record.address || "";
          if (record.village && !fullAddress.toUpperCase().includes(record.village.toUpperCase())) {
            fullAddress = `${fullAddress} ${record.village}`.trim();
          }
          const geo = await geocodeSubscriberAddress({
            address: fullAddress,
            barangay: record.brgy,
            municipality: record.municipalityname,
            latitude: null,
            longitude: null,
          });
          if (geo.success && geo.latitude && geo.longitude) {
            payload.latitude = geo.latitude;
            payload.longitude = geo.longitude;
          }
        } catch { /* keep moving to For Dispatch even if geocoding fails */ }
      }
      await api.put(`/migration/${record.id}`, payload);
      onDone();
    } catch (err) {
      setError(extractErrorMessage(err, "Failed to update call status."));
    } finally {
      setSubmitting(false);
    }
  };

  const statusColor = getCallStatusColor(selectedStatus);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold mb-2">Update Call Status</h3>
        <p className="text-sm text-gray-600 mb-3">
          Set call status to{" "}
          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${statusColor}`}>
            {selectedStatus}
          </span>
        </p>
        <div className="bg-gray-50 border rounded-lg px-3 py-2.5 mb-4 space-y-1">
          <p className="text-sm font-semibold text-gray-900" title={record?.name}>{record?.name || "—"}</p>
          <p className="text-xs text-gray-500">{record?.address || "—"}</p>
          <p className="text-xs text-gray-500"><span className="font-medium text-gray-600">Account Number:</span> {record?.oldacctno || "—"}</p>
        </div>

        {isOthers && (
          <>
            <label className="block text-sm font-medium mb-1">Custom Call Status <span className="text-red-500">*</span></label>
            <input type="text" value={customText} onChange={(e) => setCustomText(e.target.value)} placeholder="Enter custom call status…" className="w-full border rounded px-3 py-2 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </>
        )}

        {needsDispatchDate && (
          <>
            <label className="block text-sm font-medium mb-1">Dispatch Date <span className="text-red-500">*</span></label>
            <input type="date" value={dispatchDate} onChange={(e) => setDispatchDate(e.target.value)} className="w-full border rounded px-3 py-2 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </>
        )}

        {isCancelled && (
          <>
            <label className="block text-sm font-medium mb-1">Dispatch Date</label>
            <input type="date" value={getLocalToday()} disabled className="w-full border rounded px-3 py-2 text-sm mb-3 bg-gray-50 text-gray-500 cursor-not-allowed" />
          </>
        )}

        <label className="block text-sm font-medium mb-1">Call Remarks <span className="text-red-500">*</span></label>
        <textarea rows={3} value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Enter call remarks…" className="w-full border rounded px-3 py-2 text-sm mb-3 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500" />

        {isCancelled && (
          <>
            <label className="block text-sm font-medium mb-1">Cancellation Reasons <span className="text-red-500">*</span></label>
            <div className="space-y-1.5 mb-3">
              {MIG_CALL_CANCEL_REASONS.map((reason) => (
                <label key={reason} className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                  <input type="checkbox" checked={cancelReasons.includes(reason)} onChange={() => toggleReason(reason)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                  {reason}
                </label>
              ))}
            </div>
            {cancelReasons.length > 0 && remarks.trim() && (
              <p className="text-xs text-gray-500 mb-2">Will save as: <span className="font-medium">{cancelReasons.join("; ")} - {remarks.trim()}</span></p>
            )}
            <p className="text-xs text-amber-600 mb-3">Setting call status to Cancelled will also cancel this migration record.</p>
          </>
        )}

        {needsDispatchDate && (
          <p className="text-xs text-blue-600 mb-3">This will move the record to For Dispatch.</p>
        )}

        {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onCancel} className="px-4 py-2 text-sm border rounded hover:bg-gray-100">Cancel</button>
          <button type="button" disabled={!canConfirm || submitting} onClick={handleConfirm} className="px-4 py-2 text-sm text-white rounded disabled:opacity-50 bg-blue-600 hover:bg-blue-700">{submitting ? "Saving…" : "Confirm"}</button>
        </div>
      </div>
    </div>
  );
}

export default function MigrationListPage({ stage, title }) {
  const { hasPerm, user, canAccessMigrationDashboard } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const { confirm } = useConfirm();

  const canImport = hasPerm("migration.import");
  const canTag = hasPerm("migration.tag");
  const canViewChat = hasPerm("chat.view");
  const canExport = hasPerm("migration.export");
  const isUploadedPage = stage === "uploaded_for_migration";
  const isCallingPage = stage === "for_calling_scheduling";
  const isDispatchPage = stage === "for_dispatch";
  const isVisitedPage = stage === "visited_called";
  const canBulkMove = isUploadedPage || isCallingPage;

  // Date filter — used on For Dispatch and Visited/Called; defaults to today
  // Read from navigation state when toggling from map view
  const [dateFilter, setDateFilter] = useState(() => {
    if (stage === "for_dispatch" || stage === "visited_called") {
      const navFrom = location.state?.dateFrom;
      const navTo = location.state?.dateTo;
      return { from: navFrom || getLocalToday(), to: navTo || getLocalToday() };
    }
    return { from: null, to: null };
  });

  // Mobile map/page toggle (for dispatch stage only)
  const [mobileViewMode, setMobileViewMode] = useState("page");
  const [pendingMobileLocate, setPendingMobileLocate] = useState(null);
  const [pendingMobileNapFocus, setPendingMobileNapFocus] = useState(null);

  const [records, setRecords] = useState([]);
  const [confirmedRecords, setConfirmedRecords] = useState([]);
  const [cancelledRecords, setCancelledRecords] = useState([]);
  const [confirmedStatsRefresh, setConfirmedStatsRefresh] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Stats-only date filter for Calling page (does NOT affect table content)
  const [statsDateFilter, setStatsDateFilter] = useState(() =>
    stage === "for_calling_scheduling" ? { from: getLocalToday(), to: getLocalToday() } : { from: null, to: null }
  );
  const [showImport, setShowImport] = useState(false);
  const [search, setSearch] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [brgyFilter, setBrgyFilter] = useState("");
  const [brgySearch, setBrgySearch] = useState("");
  const [brgyOpen, setBrgyOpen] = useState(false);
  const brgyRef = useRef(null);
  const [bundleFilter, setBundleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState(null);
  const [pendingTeamFilter, setPendingTeamFilter] = useState(null);
  const [cancelReasonFilter, setCancelReasonFilter] = useState(null);
  const [reschedReasonFilter, setReschedReasonFilter] = useState(null);
  const [updatedByFilter, setUpdatedByFilter] = useState("");
  const [callAttemptFilter, setCallAttemptFilter] = useState(null);   // 0, 1, or 2
  const [callStatusFilter, setCallStatusFilter] = useState(null);    // "Blank","KOR","CBR","Unattended","Busy Tone","Others"
  const [myCallStatusFilter, setMyCallStatusFilter] = useState(null); // Row 2 call-status filter (user-scoped + date-scoped)
  // Collapsed state for stats sections on Calling page (default collapsed to save vertical space)
  const [overallStatsOpen, setOverallStatsOpen] = useState(false);
  const [myPerfStatsOpen, setMyPerfStatsOpen] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [detailId, setDetailId] = useState(null);
  const [chatRecordId, setChatRecordId] = useState(null);
  // Default sort:
  //   - For Dispatch stage: latest updated record on top (`updated_at desc`).
  //     This matches the NI dashboard default and is reset on every mount,
  //     so the default prevails on browser refresh and page navigation
  //     (sortStack is intentionally NOT persisted to user preferences).
  //   - Other stages: no default sort \u2014 records are shown in backend order
  //     (created_at desc).
  const [sortStack, setSortStack] = useState(
    (stage === "for_dispatch" || stage === "for_calling_scheduling")
      ? [{ key: "updated_at", dir: "desc" }]
      : []
  );
  const [actionMsg, setActionMsg] = useState("");

  // Quick status-action menu state
  const [statusMenu, setStatusMenu] = useState(null);     // { record, rect }
  const [directAction, setDirectAction] = useState(null);  // { type, record }

  const TERMINAL_ACTIONS = useMemo(() => new Set(["Completed", "Cancelled", "Rescheduled"]), []);

  const migStatusActions = useMemo(() => {
    const a = [];
    if (hasPerm("migration.complete")) a.push("complete");
    if (hasPerm("migration.cancel")) a.push("cancel");
    if (hasPerm("migration.reschedule")) a.push("reschedule");
    return a;
  }, [hasPerm]);

  const handleMigStatusClick = useCallback((r, e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setStatusMenu({ record: r, rect });
  }, []);

  const handleMigStatusAction = useCallback((action) => {
    if (!statusMenu) return;
    setDirectAction({ type: action, record: statusMenu.record });
    setStatusMenu(null);
  }, [statusMenu]);

  const handleMigDirectDone = useCallback(() => {
    setDirectAction(null);
    fetchRecords();
  }, []);

  // Quick call-status action state (for_calling_scheduling only)
  const [csMenu, setCsMenu] = useState(null);            // { record, rect }
  const [csAction, setCsAction] = useState(null);         // { record, selectedStatus }
  const canEditMigCallStatus = hasPerm("mig.field.edit.call_status");

  const handleMigCSClick = useCallback((r, e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setCsMenu({ record: r, rect });
  }, []);

  const handleMigCSSelect = useCallback((status) => {
    if (!csMenu) return;
    setCsAction({ record: csMenu.record, selectedStatus: status });
    setCsMenu(null);
  }, [csMenu]);

  const handleMigCSDone = useCallback(() => {
    setCsAction(null);
    fetchRecords();
    setConfirmedStatsRefresh((v) => v + 1);
  }, []);

  // Inline team assignment popover state
  const [teamMenu, setTeamMenu] = useState(null);          // { record, rect }
  const canEditMigTeam =
    hasPerm("migration.edit")
    && hasPerm("mig.field.edit.team_number")
    && hasPerm("mig.field.edit.personnel");
  const canEditMigTerminalTeam = hasPerm("migration.edit_terminal_team");

  const handleMigTeamClick = useCallback((r, e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTeamMenu({ record: r, rect });
  }, []);

  // Silent, in-place merge of one or more updated migration records into all
  // record lists — keeps each row's existing `updated_at` so the For Dispatch
  // `updated_at desc` sort does not bump edited rows to the top.
  const mergeUpdatedRecords = useCallback((updatedList) => {
    if (!updatedList || updatedList.length === 0) return;
    const byId = new Map(updatedList.map((u) => [u.id, u]));
    const merge = (prev) =>
      prev.map((r) => (byId.has(r.id) ? { ...r, ...byId.get(r.id), updated_at: r.updated_at } : r));
    setRecords(merge);
    setConfirmedRecords(merge);
    setCancelledRecords(merge);
  }, []);

  const handleMigTeamSave = useCallback((updated) => {
    setTeamMenu(null);
    // Quiet, in-place save (no refetch) so the page does not flash, scroll to
    // top, or re-sort — preserving the user's momentum while assigning teams,
    // the same way the company assignment saves silently. The backend returns
    // the full updated record; merge it into the matching row. `updated_at` is
    // kept so the For Dispatch `updated_at desc` sort does not bump the edited
    // row. Bulk composition changes ("Update All Records") on sibling rows only
    // appear after the next manual refresh.
    if (!updated) return;
    mergeUpdatedRecords([updated]);
  }, [mergeUpdatedRecords]);

  // Bulk "Assign Team" modal (For Dispatch page) — assign one team + personnel
  // to many team-less records at once.
  const canBulkAssignTeam = isDispatchPage && canEditMigTeam;
  // The leftmost checkbox column shows for bulk move (Uploaded/Calling) OR bulk
  // team assign (For Dispatch).
  const showSelectCol = (canTag && canBulkMove) || canBulkAssignTeam;
  const [showBulkTeamModal, setShowBulkTeamModal] = useState(false);

  const handleBulkTeamDone = useCallback((updatedList) => {
    setShowBulkTeamModal(false);
    mergeUpdatedRecords(updatedList);
    setSelected(new Set());
    if (updatedList?.length) setActionMsg(`Assigned team to ${updatedList.length} record(s).`);
  }, [mergeUpdatedRecords]);

  // Column visibility (desktop only) — mirrors NI pattern
  const PREF_KEY = `migration_${stage}_view`;
  const [hiddenColumns, setHiddenColumns] = useState(() => new Set());
  const [showColumnMenu, setShowColumnMenu] = useState(false);
  const colMenuRef = useRef(null);
  const savePrefRef = useRef(null);
  const [prefLoaded, setPrefLoaded] = useState(false);

  // Dispatch-date prompt state for bulk moves
  const [bulkDateModal, setBulkDateModal] = useState(null); // { stage, label }
  const [bulkDate, setBulkDate] = useState("");
  const [bulkDateError, setBulkDateError] = useState("");
  const [bulkJoStatus, setBulkJoStatus] = useState("");

  // Derive columns based on current stage
  const columns = useMemo(() => getColumns(stage), [stage]);

  // Visible columns for desktop table (filtered by hiddenColumns)
  const visibleColumns = useMemo(
    () => columns.filter((c) => !hiddenColumns.has(c.key)),
    [columns, hiddenColumns]
  );

  // Load saved user preferences on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/preferences/${PREF_KEY}`);
        if (cancelled || !data.value) {
          // No saved pref — apply defaultHidden columns
          const defaults = columns.filter((c) => c.defaultHidden).map((c) => c.key);
          if (defaults.length) setHiddenColumns(new Set(defaults));
          setPrefLoaded(true);
          return;
        }
        const pref = JSON.parse(data.value);
        if (pref.hiddenColumns) setHiddenColumns(new Set(pref.hiddenColumns));
      } catch { /* ignore — use defaults */ }
      if (!cancelled) setPrefLoaded(true);
    })();
    return () => { cancelled = true; };
  }, [PREF_KEY]);

  // Close column menu on outside click
  useEffect(() => {
    const handler = (e) => {
      if (colMenuRef.current && !colMenuRef.current.contains(e.target)) setShowColumnMenu(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Toggle column visibility and auto-save preferences
  const toggleColumn = (key) => {
    setHiddenColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);

      // Debounced auto-save
      clearTimeout(savePrefRef.current);
      savePrefRef.current = setTimeout(() => {
        const pref = { hiddenColumns: [...next] };
        api.put(`/preferences/${PREF_KEY}`, { value: JSON.stringify(pref) }).catch(() => {});
      }, 500);

      return next;
    });
  };

  // Export Migration to Excel
  const handleExport = useCallback(async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const params = { stage };
      if (dateFilter?.from) params.dispatch_date_from = dateFilter.from;
      if (dateFilter?.to) params.dispatch_date_to = dateFilter.to;
      const defaultName = `Migration_${stage}_${dateFilter?.from || "all"}_to_${dateFilter?.to || "all"}.xlsx`;

      // Open the native "Save As" dialog BEFORE the network request. The File
      // System Access API requires transient user activation (a recent user
      // gesture, ~5s window). Calling it after `await api.get(...)` works on
      // fast/local responses but throws a SecurityError on slow/large exports
      // (e.g. production with hundreds of records) once activation expires —
      // which then fails silently. Acquiring the handle first avoids that.
      let handle = null;
      if (window.showSaveFilePicker) {
        try {
          handle = await window.showSaveFilePicker({
            suggestedName: defaultName,
            types: [{
              description: "Excel Workbook",
              accept: { "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"] },
            }],
          });
        } catch (pickerErr) {
          if (pickerErr.name === "AbortError") return; // user cancelled
          handle = null; // unsupported/blocked — fall back to anchor download
        }
      }

      const response = await api.get("/migration/export", {
        params,
        responseType: "blob",
      });

      if (handle) {
        const writable = await handle.createWritable();
        await writable.write(response.data);
        await writable.close();
      } else {
        const url = window.URL.createObjectURL(response.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = defaultName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch {
      // silently fail
    } finally {
      setExporting(false);
    }
  }, [stage, dateFilter, exporting]);

  // Compute TEAMS count: unique team numbers per day, summed across days (mirrors NI logic)
  const teamsCount = useMemo(() => {
    const byDate = {};
    for (const r of records) {
      const raw = r.team_number;
      if (raw == null || raw === "") continue;
      const tn = String(raw).trim();
      if (tn === "") continue;
      const d = r.dispatch_date || "";
      if (!byDate[d]) byDate[d] = new Set();
      byDate[d].add(tn);
    }
    let count = 0;
    for (const d in byDate) count += byDate[d].size;
    return count;
  }, [records]);

  // Real-time migration chat notifications via SSE
  const migrationNotifRef = useRef(null);
  useChatNotifications(canViewChat, null, useCallback((ev) => {
    setRecords((prev) =>
      prev.map((r) =>
        r.id === ev.migration_record_id
          ? { ...r, chat_count: ev.chat_count, has_unread_chat: true, unread_count: (r.unread_count || 0) + 1 }
          : r
      )
    );
    migrationNotifRef.current = ev;
  }, []));

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const params = { stage };
      if (dateFilter?.from) params.dispatch_date_from = dateFilter.from;
      if (dateFilter?.to) params.dispatch_date_to = dateFilter.to;
      const { data } = await api.get("/migration/records", { params });
      setRecords(data);
    } catch {
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [stage, dateFilter]);

  // Separate fetch for confirmed stats (Calling page), keyed off statsDateFilter + refresh counter.
  //
  // PERFORMANCE: both stats sections (Overall/General + My Performance) are
  // collapsed by default on first paint, so their data is not visible until
  // the user expands one. Defer the fetch until either section has been
  // opened at least once. Once unlocked, subsequent date-filter changes or
  // refresh bumps re-fetch normally.
  const statsUnlocked = overallStatsOpen || myPerfStatsOpen;
  const statsUnlockedRef = useRef(false);
  if (statsUnlocked) statsUnlockedRef.current = true;
  // Also re-fetch after an inline call-status edit even if the user never
  // expanded a stats section — the badge counts on the collapsed headers
  // still need to be accurate the next time they expand.
  const shouldFetchStats = statsUnlockedRef.current || confirmedStatsRefresh > 0;
  useEffect(() => {
    if (stage !== "for_calling_scheduling") return;
    if (!shouldFetchStats) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/migration/calling-confirmed-stats", {
          params: {
            ...(statsDateFilter?.from ? { action_date_from: statsDateFilter.from } : {}),
            ...(statsDateFilter?.to ? { action_date_to: statsDateFilter.to } : {}),
          },
        });
        if (!cancelled) setConfirmedRecords(data);
      } catch {
        if (!cancelled) setConfirmedRecords([]);
      }
    })();
    return () => { cancelled = true; };
  }, [stage, statsDateFilter, confirmedStatsRefresh, shouldFetchStats]);

  // Separate fetch for cancelled stats (Calling page) — same deferred pattern.
  useEffect(() => {
    if (stage !== "for_calling_scheduling") return;
    if (!shouldFetchStats) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/migration/calling-cancelled-stats", {
          params: {
            ...(statsDateFilter?.from ? { action_date_from: statsDateFilter.from } : {}),
            ...(statsDateFilter?.to ? { action_date_to: statsDateFilter.to } : {}),
          },
        });
        if (!cancelled) setCancelledRecords(data);
      } catch {
        if (!cancelled) setCancelledRecords([]);
      }
    })();
    return () => { cancelled = true; };
  }, [stage, statsDateFilter, confirmedStatsRefresh, shouldFetchStats]);

  useEffect(() => {
    fetchRecords();
    setSelected(new Set());
    setActionMsg("");
  }, [fetchRecords]);

  const handleDateChange = (value) => {
    setDateFilter(value);
    setSelected(new Set());
  };

  // Multi-sort click handler: normal click = single sort; Shift+click = add/toggle layer
  const handleSortClick = useCallback((key, event) => {
    const isShift = event && event.shiftKey;

    if (isShift) {
      // Shift+click: add/toggle a secondary sort level
      setSortStack((prev) => {
        const idx = prev.findIndex((s) => s.key === key);
        if (idx >= 0) {
          const updated = [...prev];
          updated[idx] = { key, dir: updated[idx].dir === "asc" ? "desc" : "asc" };
          return updated;
        }
        return [...prev, { key, dir: "asc" }];
      });
      return;
    }

    // Normal click: replace entire sort stack
    setSortStack((prev) => {
      if (prev.length === 1 && prev[0].key === key) {
        return [{ key, dir: prev[0].dir === "asc" ? "desc" : "asc" }];
      }
      return [{ key, dir: "asc" }];
    });
  }, []);

  // Client-side multi-level sort
  const sortedRecords = useMemo(() => {
    if (!sortStack.length) return records;
    const sorted = [...records];
    sorted.sort((a, b) => {
      for (const { key, dir } of sortStack) {
        let valA = a[key];
        let valB = b[key];
        const aEmpty = valA == null || valA === "";
        const bEmpty = valB == null || valB === "";
        if (aEmpty && bEmpty) continue;
        if (aEmpty) return 1;
        if (bEmpty) return -1;
        if (typeof valA === "number" && typeof valB === "number") {
          const cmp = dir === "asc" ? valA - valB : valB - valA;
          if (cmp !== 0) return cmp;
          continue;
        }
        valA = String(valA).toLowerCase();
        valB = String(valB).toLowerCase();
        if (valA < valB) return dir === "asc" ? -1 : 1;
        if (valA > valB) return dir === "asc" ? 1 : -1;
      }
      return 0;
    });
    return sorted;
  }, [records, sortStack]);

  // Unique municipality list for filter
  const cityOptions = useMemo(() => {
    const set = new Set();
    records.forEach((r) => { if (r.municipalityname) set.add(r.municipalityname); });
    return [...set].sort();
  }, [records]);

  // Unique bundle type list for filter
  const bundleOptions = useMemo(() => {
    const set = new Set();
    records.forEach((r) => { if (r.bundletype) set.add(r.bundletype); });
    return [...set].sort();
  }, [records]);

  // Unique barangay list — scoped to selected municipality when set
  const brgyOptions = useMemo(() => {
    const set = new Set();
    const pool = cityFilter ? records.filter((r) => r.municipalityname === cityFilter) : records;
    pool.forEach((r) => { if (r.brgy) set.add(r.brgy); });
    return [...set].sort();
  }, [records, cityFilter]);

  // Unique Updated By list for filter (Calling page)
  const updatedByOptions = useMemo(() => {
    const set = new Set();
    records.forEach((r) => { if (r.updated_by_name) set.add(r.updated_by_name); });
    return [...set].sort();
  }, [records]);

  // Known call statuses for grouping
  const KNOWN_CALL_STATUSES = useMemo(() => new Set(["KOR", "CBR", "Unattended", "Busy tone"]), []);

  // Pre-stat filtered dataset: apply updated_at date range + municipality + barangay + updatedBy
  // (but NOT callAttemptFilter / callStatusFilter) so stat counts reflect dropdowns only
  const preStatFiltered = useMemo(() => {
    if (!isCallingPage) return [];
    let list = sortedRecords;
    // Filter by stats date range (updated_at = when the call status was last edited)
    // Convert UTC datetime to local date for comparison
    if (statsDateFilter?.from) list = list.filter((r) => {
      if (!r.updated_at) return false;
      const d = new Date(r.updated_at);
      const local = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      return local >= statsDateFilter.from;
    });
    if (statsDateFilter?.to) list = list.filter((r) => {
      if (!r.updated_at) return false;
      const d = new Date(r.updated_at);
      const local = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      return local <= statsDateFilter.to;
    });
    if (cityFilter) list = list.filter((r) => r.municipalityname === cityFilter);
    if (brgyFilter) list = list.filter((r) => r.brgy === brgyFilter);
    if (bundleFilter) list = list.filter((r) => r.bundletype === bundleFilter);
    if (updatedByFilter) list = list.filter((r) => r.updated_by_name === updatedByFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((r) => columns.some((c) => { const v = r[c.key]; return v && String(v).toLowerCase().includes(q); }));
    }
    return list;
  }, [isCallingPage, sortedRecords, statsDateFilter, cityFilter, brgyFilter, bundleFilter, updatedByFilter, search, columns]);

  // Records filtered by page-level filters only (NO date filter) — used for call attempt stats
  const preStatNoDate = useMemo(() => {
    if (!isCallingPage) return [];
    let list = sortedRecords;
    if (cityFilter) list = list.filter((r) => r.municipalityname === cityFilter);
    if (brgyFilter) list = list.filter((r) => r.brgy === brgyFilter);
    if (bundleFilter) list = list.filter((r) => r.bundletype === bundleFilter);
    if (updatedByFilter) list = list.filter((r) => r.updated_by_name === updatedByFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((r) => columns.some((c) => { const v = r[c.key]; return v && String(v).toLowerCase().includes(q); }));
    }
    return list;
  }, [isCallingPage, sortedRecords, cityFilter, brgyFilter, bundleFilter, updatedByFilter, search, columns]);

  // Call Attempt stats — counts from ALL page records (ignores date filter)
  const callAttemptCounts = useMemo(() => {
    const counts = { 0: 0, 1: 0, 2: 0 };
    for (const r of preStatNoDate) {
      const v = r.call_attempt_count ?? 0;
      if (v in counts) counts[v]++;
      else counts[2]++; // 2+ grouped under "2"
    }
    return counts;
  }, [preStatNoDate]);

  // Call Status stats — counts from preStatFiltered
  const callStatusCounts = useMemo(() => {
    const counts = { Blank: 0, KOR: 0, CBR: 0, Unattended: 0, "Busy Tone": 0, Others: 0 };
    for (const r of preStatFiltered) {
      const cs = r.call_status;
      if (!cs || cs.trim() === "") { counts.Blank++; continue; }
      if (cs === "KOR") { counts.KOR++; continue; }
      if (cs === "CBR") { counts.CBR++; continue; }
      if (cs === "Unattended") { counts.Unattended++; continue; }
      if (cs === "Busy tone") { counts["Busy Tone"]++; continue; }
      counts.Others++;
    }
    return counts;
  }, [preStatFiltered]);

  // Confirmed records filtered by same page-level filters (municipality, barangay, bundle, updatedBy)
  const filteredConfirmed = useMemo(() => {
    if (!isCallingPage) return [];
    let list = confirmedRecords;
    if (cityFilter) list = list.filter((r) => r.municipalityname === cityFilter);
    if (brgyFilter) list = list.filter((r) => r.brgy === brgyFilter);
    if (bundleFilter) list = list.filter((r) => r.bundletype === bundleFilter);
    if (updatedByFilter) list = list.filter((r) => r.tagged_by_name === updatedByFilter);
    return list;
  }, [isCallingPage, confirmedRecords, cityFilter, brgyFilter, bundleFilter, updatedByFilter]);

  // Overall confirmed count for Row 1
  const overallConfirmedCount = useMemo(() => filteredConfirmed.length, [filteredConfirmed]);

  // Cancelled records filtered by same page-level filters
  const filteredCancelled = useMemo(() => {
    if (!isCallingPage) return [];
    let list = cancelledRecords;
    if (cityFilter) list = list.filter((r) => r.municipalityname === cityFilter);
    if (brgyFilter) list = list.filter((r) => r.brgy === brgyFilter);
    if (bundleFilter) list = list.filter((r) => r.bundletype === bundleFilter);
    if (updatedByFilter) list = list.filter((r) => r.tagged_by_name === updatedByFilter);
    return list;
  }, [isCallingPage, cancelledRecords, cityFilter, brgyFilter, bundleFilter, updatedByFilter]);

  // Overall cancelled count for Row 1
  const overallCancelledCount = useMemo(() => filteredCancelled.length, [filteredCancelled]);

  // Determine the target user for Row 2 (individual performance)
  const targetUserId = useMemo(() => {
    if (updatedByFilter) {
      // Find the user ID from records matching the selected name
      const match = records.find((r) => r.updated_by_name === updatedByFilter);
      return match?.updated_by ?? null;
    }
    return user?.id ?? null;
  }, [updatedByFilter, records, user?.id]);

  const targetUserLabel = useMemo(() => {
    if (updatedByFilter) return updatedByFilter;
    return user?.full_name || user?.display_name || "My";
  }, [updatedByFilter, user?.full_name, user?.display_name]);

  // User-specific stats from calling-stage records
  const userPreStatFiltered = useMemo(() => {
    if (!isCallingPage || !targetUserId) return [];
    // When updatedByFilter is set, preStatFiltered is already scoped to that user
    if (updatedByFilter) return preStatFiltered;
    return preStatFiltered.filter((r) => r.updated_by === targetUserId);
  }, [isCallingPage, targetUserId, updatedByFilter, preStatFiltered]);

  // User-scoped records WITHOUT date filter — for call attempt stats
  const userPreStatNoDate = useMemo(() => {
    if (!isCallingPage || !targetUserId) return [];
    if (updatedByFilter) return preStatNoDate;
    return preStatNoDate.filter((r) => r.updated_by === targetUserId);
  }, [isCallingPage, targetUserId, updatedByFilter, preStatNoDate]);

  const userCallAttemptCounts = useMemo(() => {
    // Attempt 0 = overall count (not user-scoped), Attempt 1/2+ = user-scoped, all ignoring date filter
    const counts = { 0: callAttemptCounts[0] || 0, 1: 0, 2: 0 };
    for (const r of userPreStatNoDate) {
      const v = r.call_attempt_count ?? 0;
      if (v === 1) counts[1]++;
      else if (v >= 2) counts[2]++;
    }
    return counts;
  }, [userPreStatNoDate, callAttemptCounts]);

  const userCallStatusCounts = useMemo(() => {
    const counts = { Blank: 0, KOR: 0, CBR: 0, Unattended: 0, "Busy Tone": 0, Others: 0 };
    for (const r of userPreStatFiltered) {
      const cs = r.call_status;
      if (!cs || cs.trim() === "") { counts.Blank++; continue; }
      if (cs === "KOR") { counts.KOR++; continue; }
      if (cs === "CBR") { counts.CBR++; continue; }
      if (cs === "Unattended") { counts.Unattended++; continue; }
      if (cs === "Busy tone") { counts["Busy Tone"]++; continue; }
      counts.Others++;
    }
    return counts;
  }, [userPreStatFiltered]);

  const userConfirmedCount = useMemo(() => {
    if (!targetUserId) return 0;
    // When updatedByFilter is set, filteredConfirmed is already scoped via tagged_by_name
    if (updatedByFilter) return filteredConfirmed.length;
    return filteredConfirmed.filter((r) => r.tagged_by === targetUserId).length;
  }, [targetUserId, updatedByFilter, filteredConfirmed]);

  const userCancelledCount = useMemo(() => {
    if (!targetUserId) return 0;
    if (updatedByFilter) return filteredCancelled.length;
    return filteredCancelled.filter((r) => r.tagged_by === targetUserId).length;
  }, [targetUserId, updatedByFilter, filteredCancelled]);

  // Close barangay dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (brgyRef.current && !brgyRef.current.contains(e.target)) setBrgyOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Status counts computed from all records (before filtering) — For Dispatch only
  const statusCounts = useMemo(() => {
    const counts = { Pending: 0, "Active/Complete": 0, Rescheduled: 0, Cancelled: 0 };
    for (const r of records) {
      const s = r.jo_status;
      if (s === "Pending") counts.Pending++;
      else if (s === "Completed") counts["Active/Complete"]++;
      else if (s === "Rescheduled") counts.Rescheduled++;
      else if (s === "Cancelled") counts.Cancelled++;
    }
    return counts;
  }, [records]);

  // Pending team breakdown counts
  const pendingTeamCounts = useMemo(() => {
    let withTeam = 0;
    let withoutTeam = 0;
    for (const r of records) {
      if (r.jo_status !== "Pending") continue;
      const tn = (r.team_number ?? "").toString().trim();
      if (tn && tn !== "-") withTeam++;
      else withoutTeam++;
    }
    return { with_team: withTeam, without_team: withoutTeam };
  }, [records]);

  // Rescheduled / Cancelled reason breakdown counts
  // Uses structured JSON data when available, falls back to parsing remarks prefix
  const reasonBreakdowns = useMemo(() => {
    const RESCHED_PREFIXES = ["needs permit", "requested by customer", "subs not around", "other reason"];
    const CANCEL_PREFIXES = ["cancelled by subs", "needs rta", "full nap", "no converge facility", "for ni", "other reason"];
    const CANCEL_REMARK_MAP = { "ageing jo": "Ageing JO", "cancelled as per call": "Cancelled as per Call", "full nap": "Full NAP", "needs rta": "Needs RTA", "no converge facility": "No Converge Facility", "cancelled by subs": "Cancelled by Subs", "for ni": "For NI" };

    const parseReasons = (raw) => {
      if (!raw) return null;
      if (Array.isArray(raw)) return raw.length > 0 ? raw : null;
      try { const a = JSON.parse(raw); return Array.isArray(a) && a.length > 0 ? a : null; } catch { return null; }
    };
    const inferFromRemarks = (remarks, prefixList, remarkMap) => {
      if (!remarks) return [];
      const raw = remarks.trim();
      const prefix = raw.includes(" - ") ? raw.split(" - ")[0].trim().toLowerCase() : raw.toLowerCase();
      // Check exact prefix match against known checkbox reasons
      for (const p of prefixList) {
        if (prefix === p) return [p.split(" ").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ")];
      }
      // Check remark map for system-generated reasons
      const mapped = remarkMap[prefix];
      if (mapped) return [mapped];
      return [];
    };

    const reschedReasons = {};
    const cancelReasons = {};
    for (const r of records) {
      if (r.jo_status === "Rescheduled") {
        const structured = parseReasons(r.reschedule_reasons);
        const reasons = structured || inferFromRemarks(r.remarks, RESCHED_PREFIXES, {});
        for (const reason of reasons) reschedReasons[reason] = (reschedReasons[reason] || 0) + 1;
      } else if (r.jo_status === "Cancelled") {
        const structured = parseReasons(r.cancellation_reasons);
        const reasons = structured || inferFromRemarks(r.remarks, CANCEL_PREFIXES, CANCEL_REMARK_MAP);
        for (const reason of reasons) cancelReasons[reason] = (cancelReasons[reason] || 0) + 1;
      }
    }
    return { reschedReasons, cancelReasons };
  }, [records]);

  // Filter
  const filtered = useMemo(() => {
    let list = sortedRecords;
    if (statusFilter) {
      list = list.filter((r) => {
        if (statusFilter === "Active/Complete") return r.jo_status === "Completed";
        return r.jo_status === statusFilter;
      });
    }
    if (pendingTeamFilter) {
      list = list.filter((r) => {
        const tn = (r.team_number ?? "").toString().trim();
        if (pendingTeamFilter === "with_team") return tn && tn !== "-";
        return !tn || tn === "-";
      });
    }
    if (cancelReasonFilter) {
      const CANCEL_REMARK_MAP = { "ageing jo": "Ageing JO", "cancelled as per call": "Cancelled as per Call", "full nap": "Full NAP", "needs rta": "Needs RTA", "no converge facility": "No Converge Facility", "cancelled by subs": "Cancelled by Subs", "for ni": "For NI" };
      list = list.filter((r) => {
        let reasons = null;
        if (r.cancellation_reasons) {
          const raw = r.cancellation_reasons;
          reasons = Array.isArray(raw) ? raw : (() => { try { return JSON.parse(raw); } catch { return null; } })();
        }
        if (reasons && reasons.length > 0) return reasons.includes(cancelReasonFilter);
        // Fallback: match from remarks
        if (!r.remarks) return false;
        const prefix = r.remarks.includes(" - ") ? r.remarks.split(" - ")[0].trim().toLowerCase() : r.remarks.trim().toLowerCase();
        const mapped = CANCEL_REMARK_MAP[prefix] || prefix.split(" ").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
        return mapped === cancelReasonFilter;
      });
    }
    if (reschedReasonFilter) {
      list = list.filter((r) => {
        let reasons = null;
        if (r.reschedule_reasons) {
          const raw = r.reschedule_reasons;
          reasons = Array.isArray(raw) ? raw : (() => { try { return JSON.parse(raw); } catch { return null; } })();
        }
        if (reasons && reasons.length > 0) return reasons.includes(reschedReasonFilter);
        // Fallback: match from remarks prefix
        if (!r.remarks) return false;
        const prefix = r.remarks.includes(" - ") ? r.remarks.split(" - ")[0].trim().toLowerCase() : "";
        const matched = prefix.split(" ").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
        return matched === reschedReasonFilter;
      });
    }
    if (cityFilter) {
      list = list.filter((r) => r.municipalityname === cityFilter);
    }
    if (brgyFilter) {
      list = list.filter((r) => r.brgy === brgyFilter);
    }
    if (bundleFilter) {
      list = list.filter((r) => r.bundletype === bundleFilter);
    }
    if (updatedByFilter) {
      list = list.filter((r) => r.updated_by_name === updatedByFilter);
    }
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((r) =>
        columns.some((c) => {
          const v = r[c.key];
          return v && String(v).toLowerCase().includes(q);
        })
      );
    }
    // Call Attempt stat filter (Calling page)
    if (callAttemptFilter !== null) {
      if (callAttemptFilter === 2) {
        list = list.filter((r) => (r.call_attempt_count ?? 0) >= 2);
      } else {
        list = list.filter((r) => (r.call_attempt_count ?? 0) === callAttemptFilter);
      }
    }
    // Call Status stat filter (Calling page) — also applies stats date range
    if (callStatusFilter) {
      const _toLocal = (iso) => { const d = new Date(iso); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`; };
      list = list.filter((r) => {
        const cs = r.call_status;
        let match = false;
        if (callStatusFilter === "Blank") match = !cs || cs.trim() === "";
        else if (callStatusFilter === "KOR") match = cs === "KOR";
        else if (callStatusFilter === "CBR") match = cs === "CBR";
        else if (callStatusFilter === "Unattended") match = cs === "Unattended";
        else if (callStatusFilter === "Busy Tone") match = cs === "Busy tone";
        else match = cs && cs.trim() !== "" && cs !== "KOR" && cs !== "CBR" && cs !== "Unattended" && cs !== "Busy tone";
        if (!match) return false;
        // stats date range
        if (statsDateFilter?.from && r.updated_at) { if (_toLocal(r.updated_at) < statsDateFilter.from) return false; }
        if (statsDateFilter?.to && r.updated_at) { if (_toLocal(r.updated_at) > statsDateFilter.to) return false; }
        return true;
      });
    }
    // Row 2 user-scoped call status filter (user + status + stats date)
    if (myCallStatusFilter) {
      const _toLocal = (iso) => { const d = new Date(iso); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`; };
      list = list.filter((r) => {
        // user scope
        if (r.updated_by_name !== targetUserLabel) return false;
        // call status match
        const cs = r.call_status;
        if (myCallStatusFilter === "Blank") { if (cs && cs.trim() !== "") return false; }
        else if (myCallStatusFilter === "KOR") { if (cs !== "KOR") return false; }
        else if (myCallStatusFilter === "CBR") { if (cs !== "CBR") return false; }
        else if (myCallStatusFilter === "Unattended") { if (cs !== "Unattended") return false; }
        else if (myCallStatusFilter === "Busy Tone") { if (cs !== "Busy tone") return false; }
        else { /* Others */ if (!cs || cs.trim() === "" || cs === "KOR" || cs === "CBR" || cs === "Unattended" || cs === "Busy tone") return false; }
        // stats date range
        if (statsDateFilter?.from && r.updated_at) { if (_toLocal(r.updated_at) < statsDateFilter.from) return false; }
        if (statsDateFilter?.to && r.updated_at) { if (_toLocal(r.updated_at) > statsDateFilter.to) return false; }
        return true;
      });
    }
    return list;
  }, [sortedRecords, search, cityFilter, brgyFilter, bundleFilter, statusFilter, pendingTeamFilter, cancelReasonFilter, reschedReasonFilter, updatedByFilter, callAttemptFilter, callStatusFilter, myCallStatusFilter, statsDateFilter, targetUserLabel, columns]);

  // A row already has a team when Team Number is set. On the For Dispatch page
  // only team-less rows are selectable for bulk "Assign Team".
  const rowHasTeam = (r) => !!(r.team_number && String(r.team_number).trim());
  const isRowSelectable = (r) => (canBulkAssignTeam ? !rowHasTeam(r) : true);

  const allIds = filtered.filter(isRowSelectable).map((r) => r.id);
  const allSelected = allIds.length > 0 && allIds.every((id) => selected.has(id));

  // Effective ids for bulk "Assign Team": selected rows that are still team-less
  // (a row may gain a team via the inline popover while it sits in `selected`).
  const bulkAssignIds = useMemo(() => {
    if (!canBulkAssignTeam || selected.size === 0) return [];
    const byId = new Map(records.map((r) => [r.id, r]));
    return [...selected].filter((id) => {
      const r = byId.get(id);
      return r && !(r.team_number && String(r.team_number).trim());
    });
  }, [canBulkAssignTeam, records, selected]);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        allIds.forEach((id) => next.delete(id));
      } else {
        allIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  // Bulk tag
  const NEEDS_DATE_MODAL = new Set(["for_dispatch", "visited_called"]);

  const handleBulkTag = async (targetStage) => {
    if (selected.size === 0) return;
    const label = STAGE_LABELS[targetStage] || targetStage;

    if (NEEDS_DATE_MODAL.has(targetStage)) {
      // Open dispatch-date (+ optional JO Status) modal
      setBulkDateModal({ stage: targetStage, label });
      setBulkDate("");
      setBulkDateError("");
      setBulkJoStatus("");
      return;
    }

    if (!(await confirm({
      title: "Confirm Move",
      message: `Move ${selected.size} record(s) to "${label}"?`,
      confirmLabel: "Move",
    }))) return;
    try {
      const { data } = await api.post("/migration/bulk-tag", {
        record_ids: [...selected],
        target_stage: targetStage,
      });
      setActionMsg(`Moved ${data.updated} record(s) to ${label}.`);
      setSelected(new Set());
      await fetchRecords();
    } catch (err) {
      setActionMsg("Error: " + (err.response?.data?.detail || err.message));
    }
  };

  const handleBulkDateConfirm = async () => {
    if (!bulkDateModal) return;
    const { stage, label } = bulkDateModal;

    if (!bulkDate) {
      setBulkDateError("Dispatch date is required.");
      return;
    }
    const today = new Date();
    const localToday = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    if (stage === "for_dispatch" && bulkDate < localToday) {
      setBulkDateError("Dispatch date cannot be in the past.");
      return;
    }
    if (stage === "visited_called" && bulkDate > localToday) {
      setBulkDateError("Dispatch date cannot be in the future.");
      return;
    }
    if (stage === "visited_called" && !isCallingPage && !bulkJoStatus) {
      setBulkDateError("JO Status is required.");
      return;
    }

    try {
      const body = {
        record_ids: [...selected],
        target_stage: stage,
        dispatch_date: bulkDate,
      };
      if (stage === "visited_called" && bulkJoStatus) {
        body.jo_status = bulkJoStatus;
      }
      const { data } = await api.post("/migration/bulk-tag", body);
      setActionMsg(`Moved ${data.updated} record(s) to ${label}.`);
      setSelected(new Set());
      setBulkDateModal(null);
      await fetchRecords();
    } catch (err) {
      setBulkDateError(err.response?.data?.detail || err.message);
    }
  };

  // Mobile card renderer
  const renderMobileCard = (r, idx) => {
    return (
      <button
        key={r.reschedule_snapshot_id ? `rs-${r.reschedule_snapshot_id}` : r.id}
        className="w-full text-left bg-white rounded-xl border shadow-sm p-3.5 active:bg-gray-50 transition-colors overflow-hidden"
        onClick={() => setDetailId(r.id)}
      >
        {/* Line 1: Count + Name + Status */}
        <div className="flex items-start justify-between gap-2 mb-1 min-w-0">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-100 text-gray-500 text-[11px] font-bold flex items-center justify-center">
              {idx + 1}
            </span>
            {isMobile && isDispatchPage ? (
              <span
                className="text-sm font-semibold text-blue-600 truncate"
                onClick={(e) => {
                  e.stopPropagation();
                  setPendingMobileLocate({
                    id: r.id,
                    customerName: r.name,
                    address: r.address,
                    municipality: r.municipalityname,
                    barangay: r.brgy,
                    latitude: r.latitude || null,
                    longitude: r.longitude || null,
                  });
                  setMobileViewMode("map");
                }}
              >
                {r.name || "—"}
              </span>
            ) : (
              <p className="text-sm font-semibold text-gray-900 truncate">
                {r.name || "—"}
              </p>
            )}
            {r.is_priority && (
              <span
                title="Submitted by subscriber via public migration form"
                className="flex-shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-amber-100 text-amber-800 border border-amber-300"
              >
                <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.955a1 1 0 00.95.69h4.16c.969 0 1.371 1.24.588 1.81l-3.365 2.445a1 1 0 00-.364 1.118l1.286 3.955c.3.921-.755 1.688-1.54 1.118L10 14.347l-3.365 2.446c-.784.57-1.838-.197-1.539-1.118l1.286-3.955a1 1 0 00-.364-1.118L2.654 8.157c-.783-.57-.38-1.81.588-1.81h4.16a1 1 0 00.95-.69l1.286-3.955z"/></svg>
                Prio
              </span>
            )}
          </div>
          {r.jo_status && (() => {
            const isTerminalStatus = r.jo_status === "Completed" || r.jo_status === "Cancelled";
            const canQuickStatus = isDispatchPage && !isTerminalStatus && migStatusActions.length > 0;
            if (canQuickStatus) {
              return (
                <span
                  onClick={(e) => { e.stopPropagation(); handleMigStatusClick(r, e); }}
                  className={`flex-shrink-0 inline-block text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${getJoStatusColor(r.jo_status)}`}
                >
                  {r.jo_status}
                  {(() => { const a = isDispatchPage ? calculatePendingAgeing({ ...r, job_date: r.dispatch_date }, getLocalToday()) : null; return a !== null ? <sup className={`text-[0.65em] ml-0.5 font-bold ${getAgeingColor(a)}`}>{a}</sup> : null; })()}
                </span>
              );
            }
            return (
              <span className={`flex-shrink-0 inline-block text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${getJoStatusColor(r.jo_status)}`}>
                {r.jo_status}
                {(() => { const a = isDispatchPage ? calculatePendingAgeing({ ...r, job_date: r.dispatch_date }, getLocalToday()) : null; return a !== null ? <sup className={`text-[0.65em] ml-0.5 font-bold ${getAgeingColor(a)}`}>{a}</sup> : null; })()}
              </span>
            );
          })()}
        </div>
        {/* Line 2: Barangay + Call Status + Date */}
        <div className="flex items-center gap-2 text-xs text-gray-500 mb-1 min-w-0">
          {r.brgy && (
            <span className="truncate flex items-center gap-1 min-w-0">
              <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span className="truncate">{r.brgy}</span>
            </span>
          )}
          {(() => {
            const canQuickCS = isCallingPage && canEditMigCallStatus;
            const csLabel = r.call_status ? fmtCallStatus(r) : "—";
            const csBadge = r.call_status ? getCallStatusColor(r.call_status) : "bg-gray-100 text-gray-400";
            return (
              <span
                className={`flex-shrink-0 inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded-full max-w-[40%] truncate ${csBadge}${canQuickCS ? " cursor-pointer" : ""}`}
                onClick={canQuickCS ? (e) => {
                  e.stopPropagation();
                  const rect = e.currentTarget.getBoundingClientRect();
                  setCsMenu({ record: r, rect });
                } : undefined}
              >
                {csLabel}
              </span>
            );
          })()}
        </div>
        {/* Line 3: Personnel + Contact + Chat */}
        <div className="flex items-center gap-3 text-xs text-gray-600 min-w-0">
          <span className="truncate flex items-center gap-1 min-w-0">
            <svg className="w-3 h-3 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            <span className="truncate">{r.personnel || "—"}</span>
          </span>
          {r.contact_info && (
            <span className="truncate flex items-center gap-1 min-w-0">
              <svg className="w-3 h-3 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              <span className="truncate">{cleanContact(r.contact_info)}</span>
            </span>
          )}
          {canViewChat && (
            <button
              onClick={(e) => { e.stopPropagation(); setChatRecordId(r.id); }}
              className={`ml-auto flex-shrink-0 flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[11px] font-bold ${
                (r.chat_count || 0) === 0
                  ? "bg-gray-100 text-gray-400"
                  : r.has_unread_chat
                    ? "bg-red-100 text-red-700"
                    : "bg-green-100 text-green-700"
              }`}
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              {r.has_unread_chat ? (r.unread_count || 0) : (r.chat_count || 0)}
            </button>
          )}
        </div>
      </button>
    );
  };

  return (
    <div className="h-screen flex flex-col bg-gray-100 overflow-hidden">
      {isMobile ? <MobileNavbar /> : <Navbar />}
      {/* Module accent strip */}
      {!isMobile && <div className={`h-1 ${MIG.strip} flex-shrink-0`} />}

      <main className={`flex-1 flex flex-col min-h-0 pt-3 pb-2 w-full relative ${isMobile ? "px-0" : "px-4 lg:px-6"}`}>
        {/* View toggle — anchored top-right for consistent positioning across Table/Map views */}
        {!isMobile && isDispatchPage && canAccessMigrationDashboard && (
          <div
            className="absolute top-3 right-4 lg:right-6 z-20 flex rounded-lg border border-gray-300 overflow-hidden cursor-pointer"
            onClick={() => navigate("/migration/dispatch-dashboard", { state: { dateFrom: dateFilter?.from, dateTo: dateFilter?.to } })}
          >
            <span className={`px-3 py-1.5 text-sm font-medium ${MIG.btnPrimaryStatic} flex items-center gap-1`}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 6h18M3 14h18M3 18h18" /></svg>
              Table
            </span>
            <span className="px-3 py-1.5 text-sm font-medium bg-white text-gray-600 flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" /></svg>
              Map
            </span>
          </div>
        )}

        {/* Toolbar */}
        <div className={`flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-3 flex-shrink-0 px-3 md:pl-0${isDispatchPage && canAccessMigrationDashboard ? " md:pr-[180px]" : " md:pr-0"}`}>
          <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <input
                type="text"
                placeholder="Search…"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setSelected(new Set()); }}
                className={`border rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${isMobile ? "w-full" : "w-64"}`}
              />
              {search && (
                <button onClick={() => setSearch("")} className="text-gray-400 hover:text-gray-600 text-lg">×</button>
              )}
            </div>
            {isDispatchPage && !isMobile && (
              <DateFilter value={dateFilter} onChange={handleDateChange} accent="purple" showViewAll={false} />
            )}
            {isVisitedPage && !isMobile && (
              <DateFilter value={dateFilter} onChange={handleDateChange} accent="purple" showViewAll={false} />
            )}
            {isCallingPage && !isMobile && (
              <DateFilter value={statsDateFilter} onChange={setStatsDateFilter} accent="purple" showViewAll={false} />
            )}
            <select
              value={cityFilter}
              onChange={(e) => { setCityFilter(e.target.value); setBrgyFilter(""); setBrgySearch(""); setSelected(new Set()); }}
              className={`border rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${(isDispatchPage || isVisitedPage || isCallingPage) ? "hidden" : "hidden md:block"}`}
            >
              <option value="">All Municipalities</option>
              {cityOptions.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <div className={`relative ${(isDispatchPage || isVisitedPage || isCallingPage) ? "hidden" : "hidden md:block"}`} ref={brgyRef}>
              <div
                onClick={() => setBrgyOpen((o) => !o)}
                className="border rounded px-3 py-1.5 text-sm cursor-pointer min-w-[160px] bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center justify-between gap-1"
              >
                <span className={brgyFilter ? "text-gray-900" : "text-gray-500"}>{brgyFilter || "All Barangays"}</span>
                <span className="text-gray-400 text-xs">▼</span>
              </div>
              {brgyOpen && (
                <div className="absolute z-50 mt-1 bg-white border rounded shadow-lg w-64 max-h-60 flex flex-col">
                  <input
                    type="text"
                    placeholder="Search barangay…"
                    value={brgySearch}
                    onChange={(e) => setBrgySearch(e.target.value)}
                    className="border-b px-3 py-1.5 text-sm focus:outline-none"
                    autoFocus
                  />
                  <div className="overflow-y-auto">
                    <div
                      onClick={() => { setBrgyFilter(""); setBrgySearch(""); setBrgyOpen(false); setSelected(new Set()); }}
                      className={`px-3 py-1.5 text-sm cursor-pointer hover:bg-blue-50 ${!brgyFilter ? "bg-blue-100 font-medium" : ""}`}
                    >All Barangays</div>
                    {brgyOptions
                      .filter((b) => !brgySearch || b.toLowerCase().includes(brgySearch.toLowerCase()))
                      .map((b) => (
                        <div
                          key={b}
                          onClick={() => { setBrgyFilter(b); setBrgySearch(""); setBrgyOpen(false); setSelected(new Set()); }}
                          className={`px-3 py-1.5 text-sm cursor-pointer hover:bg-blue-50 ${brgyFilter === b ? "bg-blue-100 font-medium" : ""}`}
                        >{b}</div>
                      ))
                    }
                  </div>
                </div>
              )}
            </div>
            {(isUploadedPage || isCallingPage) && (
              <select
                value={bundleFilter}
                onChange={(e) => { setBundleFilter(e.target.value); setSelected(new Set()); }}
                className="border rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 hidden md:block"
              >
                <option value="">All Bundle Types</option>
                {bundleOptions.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            )}
            {isCallingPage && (
              <select
                value={updatedByFilter}
                onChange={(e) => { setUpdatedByFilter(e.target.value); setSelected(new Set()); }}
                className="border rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 hidden md:block"
              >
                <option value="">All Updated By</option>
                {updatedByOptions.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            )}
          </div>
          {!isMobile && (
            <div className="flex gap-2 items-center justify-end flex-wrap">
              {/* Bulk Assign Team — For Dispatch page; appears only when
                  team-less rows are selected. Selecting a row that already has
                  a team is blocked at the checkbox, so this is always valid. */}
              {canBulkAssignTeam && bulkAssignIds.length > 0 && (
                <button
                  onClick={() => setShowBulkTeamModal(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white text-sm px-3 py-1.5 rounded flex items-center gap-1 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  title="Assign the same team & personnel to all selected records"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-1.13a4 4 0 10-4-4 4 4 0 004 4z" /></svg>
                  Assign Team ({bulkAssignIds.length})
                </button>
              )}
              {/* Export to Excel — permission-gated */}
              {canExport && (
                <button
                  onClick={handleExport}
                  disabled={exporting}
                  className="border rounded px-3 py-1.5 text-sm bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500 flex items-center gap-1 disabled:opacity-50"
                  title="Export Migration to Excel"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                  {exporting ? "Exporting…" : "Export"}
                </button>
              )}
              {/* Column visibility toggle */}
              <div className="relative" ref={colMenuRef}>
                <button
                  onClick={() => setShowColumnMenu((v) => !v)}
                  className="border rounded px-3 py-1.5 text-sm bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center gap-1"
                  title="Show/Hide Columns"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h18M3 8h18M3 12h18M3 16h18M3 20h18" /></svg>
                  Columns
                </button>
                {showColumnMenu && (
                  <div className="absolute right-0 mt-1 bg-white border rounded-lg shadow-lg z-50 w-56 max-h-80 overflow-y-auto py-1">
                    {columns.filter((c) => c.hideable).map((col) => (
                      <label
                        key={col.key}
                        className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer select-none"
                      >
                        <input
                          type="checkbox"
                          checked={!hiddenColumns.has(col.key)}
                          onChange={() => toggleColumn(col.key)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        {col.label}
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Teams count box */}
              {isDispatchPage && (
                <div className="inline-flex items-center gap-2 rounded border border-teal-300 bg-teal-100 px-3 py-1.5 select-none whitespace-nowrap leading-none">
                  <span className="text-xs font-semibold uppercase tracking-wide text-teal-800">Teams</span>
                  <span className="text-sm font-bold text-teal-900 tabular-nums">{teamsCount}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Stats cards — For Dispatch page only (desktop; mobile stats are in scroll container) */}
        {isDispatchPage && !isMobile && (
          <div className="flex items-stretch gap-2 mb-3 flex-shrink-0">
            <div className={`flex-shrink-0 ${MIG.badge} rounded-lg shadow-sm flex items-center gap-2 px-4 py-2`}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d={MIG.iconPath} /></svg>
              <span className="text-xs font-bold uppercase tracking-wider">Migration</span>
            </div>
            <div className="grid grid-cols-4 gap-2 flex-1">
            {/* Pending card — enhanced with team breakdown */}
            {(() => {
              const isPendingActive = statusFilter === "Pending" && !pendingTeamFilter;
              const isWithTeamActive = statusFilter === "Pending" && pendingTeamFilter === "with_team";
              const isWithoutTeamActive = statusFilter === "Pending" && pendingTeamFilter === "without_team";
              const pendingCount = statusCounts.Pending || 0;
              const withTeam = pendingTeamCounts.with_team || 0;
              const withoutTeam = pendingTeamCounts.without_team || 0;
              const handlePendingClick = () => {
                setSearch("");
                setCancelReasonFilter(null);
                setReschedReasonFilter(null);
                if (isPendingActive) { setStatusFilter(null); setPendingTeamFilter(null); }
                else { setStatusFilter("Pending"); setPendingTeamFilter(null); }
              };
              const handleTeamClick = (type) => (e) => {
                e.stopPropagation();
                setSearch("");
                setCancelReasonFilter(null);
                setReschedReasonFilter(null);
                if (pendingTeamFilter === type) { setStatusFilter(null); setPendingTeamFilter(null); }
                else { setStatusFilter("Pending"); setPendingTeamFilter(type); }
              };
              return (
                <div
                  className={`rounded-lg px-3 py-2 text-center border bg-yellow-100 text-yellow-800 transition-all hover:shadow cursor-pointer focus-within:ring-2 focus-within:ring-blue-400 ${
                    (isPendingActive || isWithTeamActive || isWithoutTeamActive) ? 'ring-2 ring-blue-600 border-blue-600 shadow-md' : ''
                  }`}
                  style={{ opacity: pendingCount === 0 ? 0.5 : 1 }}
                >
                  <div onClick={pendingCount > 0 ? handlePendingClick : undefined} tabIndex={0} title="Show only Pending records"
                    className={`cursor-pointer ${isPendingActive ? 'font-black' : ''}`}>
                    <div className="flex items-baseline justify-center gap-1.5 leading-tight">
                      <span className="text-xs font-semibold uppercase tracking-wide text-gray-700">Pending</span>
                      <span className="text-lg font-bold text-gray-900 tabular-nums">{pendingCount}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-center gap-1.5 mt-0.5 border-t border-yellow-300 pt-1">
                    <button onClick={handleTeamClick("with_team")} disabled={withTeam === 0}
                      className={`text-[10px] leading-tight px-1.5 py-0.5 rounded transition-all ${
                        isWithTeamActive ? 'bg-yellow-500 text-white font-bold shadow-sm' : 'hover:bg-yellow-200 text-yellow-900'
                      } ${withTeam === 0 ? 'opacity-40 cursor-default' : 'cursor-pointer'}`}
                      title="Show pending with team assigned"
                    >
                      <span className="font-semibold">{withTeam}</span> w/ Team
                    </button>
                    <span className="text-yellow-400 text-[10px]">|</span>
                    <button onClick={handleTeamClick("without_team")} disabled={withoutTeam === 0}
                      className={`text-[10px] leading-tight px-1.5 py-0.5 rounded transition-all ${
                        isWithoutTeamActive ? 'bg-yellow-500 text-white font-bold shadow-sm' : 'hover:bg-yellow-200 text-yellow-900'
                      } ${withoutTeam === 0 ? 'opacity-40 cursor-default' : 'cursor-pointer'}`}
                      title="Show pending without team assigned"
                    >
                      <span className="font-semibold">{withoutTeam}</span> w/o Team
                    </button>
                  </div>
                </div>
              );
            })()}
            {/* Active/Complete card */}
            <button
              className={`rounded-lg px-3 py-2 text-center border bg-green-100 text-green-800 transition-all hover:shadow cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                statusFilter === "Active/Complete" ? 'ring-2 ring-blue-600 border-blue-600 shadow-md' : ''
              }`}
              style={{ opacity: statusCounts["Active/Complete"] === 0 ? 0.5 : 1 }}
              disabled={statusCounts["Active/Complete"] === 0}
              onClick={() => {
                setSearch("");
                setPendingTeamFilter(null);
                setCancelReasonFilter(null);
                setReschedReasonFilter(null);
                setStatusFilter(prev => prev === "Active/Complete" ? null : "Active/Complete");
              }}
              tabIndex={0}
              title="Show only Active/Complete records"
            >
              <div className="flex items-baseline justify-center gap-1.5 leading-tight">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-700">Active/Complete</span>
                <span className="text-lg font-bold text-gray-900 tabular-nums">{statusCounts["Active/Complete"] || 0}</span>
              </div>
            </button>
            {/* Rescheduled card — enhanced with reason breakdown */}
            {(() => {
              const reschedCount = statusCounts.Rescheduled || 0;
              const rCounts = reasonBreakdowns.reschedReasons;
              const isMainActive = statusFilter === "Rescheduled" && !reschedReasonFilter;
              const anyActive = statusFilter === "Rescheduled";
              const handleMainClick = () => {
                setSearch("");
                setPendingTeamFilter(null);
                setCancelReasonFilter(null);
                setReschedReasonFilter(null);
                if (isMainActive) setStatusFilter(null);
                else setStatusFilter("Rescheduled");
              };
              const handleReasonClick = (reason) => (e) => {
                e.stopPropagation();
                setSearch("");
                setPendingTeamFilter(null);
                setCancelReasonFilter(null);
                if (reschedReasonFilter === reason) { setStatusFilter(null); setReschedReasonFilter(null); }
                else { setStatusFilter("Rescheduled"); setReschedReasonFilter(reason); }
              };
              const reasonEntries = Object.entries(rCounts).filter(([, c]) => c > 0);
              return (
                <div
                  className={`rounded-lg px-3 py-2 text-center border bg-blue-100 text-blue-800 transition-all hover:shadow cursor-pointer focus-within:ring-2 focus-within:ring-blue-400 ${
                    anyActive ? 'ring-2 ring-blue-600 border-blue-600 shadow-md' : ''
                  }`}
                  style={{ opacity: reschedCount === 0 ? 0.5 : 1 }}
                >
                  <div onClick={reschedCount > 0 ? handleMainClick : undefined} tabIndex={0} title="Show only Rescheduled records"
                    className={`cursor-pointer ${isMainActive ? 'font-black' : ''}`}>
                    <div className="flex items-baseline justify-center gap-1.5 leading-tight">
                      <span className="text-xs font-semibold uppercase tracking-wide text-gray-700">Rescheduled</span>
                      <span className="text-lg font-bold text-gray-900 tabular-nums">{reschedCount}</span>
                    </div>
                  </div>
                  {reasonEntries.length > 0 && (
                    <div className="flex flex-wrap items-center justify-center gap-1 mt-0.5 border-t border-blue-300 pt-1">
                      {reasonEntries.map(([reason, count], i) => (
                        <span key={reason} className="flex items-center gap-0">
                          {i > 0 && <span className="text-blue-400 text-[10px] mx-0.5">|</span>}
                          <button onClick={handleReasonClick(reason)} disabled={count === 0}
                            className={`text-[10px] leading-tight px-1 py-0.5 rounded transition-all ${
                              reschedReasonFilter === reason ? 'bg-blue-600 text-white font-bold shadow-sm' : 'hover:bg-blue-200 text-blue-900'
                            } cursor-pointer`}
                            title={`Show rescheduled: ${reason}`}
                          >
                            <span className="font-semibold">{count}</span> {reason}
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
            {/* Cancelled card — enhanced with reason breakdown */}
            {(() => {
              const cancelCount = statusCounts.Cancelled || 0;
              const cCounts = reasonBreakdowns.cancelReasons;
              const isMainActive = statusFilter === "Cancelled" && !cancelReasonFilter;
              const anyActive = statusFilter === "Cancelled";
              const handleMainClick = () => {
                setSearch("");
                setPendingTeamFilter(null);
                setReschedReasonFilter(null);
                setCancelReasonFilter(null);
                if (isMainActive) setStatusFilter(null);
                else setStatusFilter("Cancelled");
              };
              const handleReasonClick = (reason) => (e) => {
                e.stopPropagation();
                setSearch("");
                setPendingTeamFilter(null);
                setReschedReasonFilter(null);
                if (cancelReasonFilter === reason) { setStatusFilter(null); setCancelReasonFilter(null); }
                else { setStatusFilter("Cancelled"); setCancelReasonFilter(reason); }
              };
              const reasonEntries = Object.entries(cCounts).filter(([, c]) => c > 0);
              return (
                <div
                  className={`rounded-lg px-3 py-2 text-center border bg-red-100 text-red-800 transition-all hover:shadow cursor-pointer focus-within:ring-2 focus-within:ring-blue-400 ${
                    anyActive ? 'ring-2 ring-blue-600 border-blue-600 shadow-md' : ''
                  }`}
                  style={{ opacity: cancelCount === 0 ? 0.5 : 1 }}
                >
                  <div onClick={cancelCount > 0 ? handleMainClick : undefined} tabIndex={0} title="Show only Cancelled records"
                    className={`cursor-pointer ${isMainActive ? 'font-black' : ''}`}>
                    <div className="flex items-baseline justify-center gap-1.5 leading-tight">
                      <span className="text-xs font-semibold uppercase tracking-wide text-gray-700">Cancelled</span>
                      <span className="text-lg font-bold text-gray-900 tabular-nums">{cancelCount}</span>
                    </div>
                  </div>
                  {reasonEntries.length > 0 && (
                    <div className="flex flex-wrap items-center justify-center gap-1 mt-0.5 border-t border-red-300 pt-1">
                      {reasonEntries.map(([reason, count], i) => (
                        <span key={reason} className="flex items-center gap-0">
                          {i > 0 && <span className="text-red-400 text-[10px] mx-0.5">|</span>}
                          <button onClick={handleReasonClick(reason)} disabled={count === 0}
                            className={`text-[10px] leading-tight px-1 py-0.5 rounded transition-all ${
                              cancelReasonFilter === reason ? 'bg-red-600 text-white font-bold shadow-sm' : 'hover:bg-red-200 text-red-900'
                            } cursor-pointer`}
                            title={`Show cancelled: ${reason}`}
                          >
                            <span className="font-semibold">{count}</span> {reason}
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
            </div>
          </div>
        )}

        {/* Record count — mobile dispatch only (rendered inside scroll container, not here) */}

        {/* Stats cards — Completed/Visited/Called page only (desktop) */}
        {isVisitedPage && !isMobile && (
          <div className="flex items-stretch gap-2 mb-3 flex-shrink-0">
            <div className={`flex-shrink-0 ${MIG.badge} rounded-lg shadow-sm flex items-center gap-2 px-4 py-2`}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d={MIG.iconPath} /></svg>
              <span className="text-xs font-bold uppercase tracking-wider">Migration</span>
            </div>
            <div className="grid grid-cols-2 gap-2 flex-1">
              {/* Completed card */}
              <button
                className={`rounded-lg px-3 py-2 text-center border bg-green-100 text-green-800 transition-all hover:shadow cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                  statusFilter === "Active/Complete" ? 'ring-2 ring-blue-600 border-blue-600 shadow-md' : ''
                }`}
                style={{ opacity: statusCounts["Active/Complete"] === 0 ? 0.5 : 1 }}
                disabled={statusCounts["Active/Complete"] === 0}
                onClick={() => {
                  setSearch("");
                  setCancelReasonFilter(null);
                  setReschedReasonFilter(null);
                  setStatusFilter(prev => prev === "Active/Complete" ? null : "Active/Complete");
                }}
              tabIndex={0}
              title="Show only Completed records"
            >
              <div className="flex items-baseline justify-center gap-1.5 leading-tight">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-700">Completed</span>
                <span className="text-lg font-bold text-gray-900 tabular-nums">{statusCounts["Active/Complete"] || 0}</span>
              </div>
            </button>
              {/* Cancelled card */}
              {(() => {
                const cancelCount = statusCounts.Cancelled || 0;
                const cCounts = reasonBreakdowns.cancelReasons;
                const isMainActive = statusFilter === "Cancelled" && !cancelReasonFilter;
                const anyActive = statusFilter === "Cancelled";
                const handleMainClick = () => {
                  setSearch("");
                  setReschedReasonFilter(null);
                  setCancelReasonFilter(null);
                  if (isMainActive) setStatusFilter(null);
                  else setStatusFilter("Cancelled");
                };
                const handleReasonClick = (reason) => (e) => {
                  e.stopPropagation();
                  setSearch("");
                  setReschedReasonFilter(null);
                  if (cancelReasonFilter === reason) { setStatusFilter(null); setCancelReasonFilter(null); }
                  else { setStatusFilter("Cancelled"); setCancelReasonFilter(reason); }
                };
                const reasonEntries = Object.entries(cCounts).filter(([, c]) => c > 0);
                return (
                  <div
                    className={`rounded-lg px-3 py-2 text-center border bg-red-100 text-red-800 transition-all hover:shadow cursor-pointer focus-within:ring-2 focus-within:ring-blue-400 ${
                      anyActive ? 'ring-2 ring-blue-600 border-blue-600 shadow-md' : ''
                    }`}
                    style={{ opacity: cancelCount === 0 ? 0.5 : 1 }}
                  >
                    <div onClick={cancelCount > 0 ? handleMainClick : undefined} tabIndex={0} title="Show only Cancelled records"
                      className={`cursor-pointer ${isMainActive ? 'font-black' : ''}`}>
                      <div className="flex items-baseline justify-center gap-1.5 leading-tight">
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-700">Cancelled</span>
                        <span className="text-lg font-bold text-gray-900 tabular-nums">{cancelCount}</span>
                      </div>
                    </div>
                    {reasonEntries.length > 0 && (
                      <div className="flex flex-wrap items-center justify-center gap-1 mt-0.5 border-t border-red-300 pt-1">
                        {reasonEntries.map(([reason, count], i) => (
                          <span key={reason} className="flex items-center gap-0">
                            {i > 0 && <span className="text-red-400 text-[10px] mx-0.5">|</span>}
                            <button onClick={handleReasonClick(reason)} disabled={count === 0}
                              className={`text-[10px] leading-tight px-1 py-0.5 rounded transition-all ${
                                cancelReasonFilter === reason ? 'bg-red-600 text-white font-bold shadow-sm' : 'hover:bg-red-200 text-red-900'
                              } cursor-pointer`}
                              title={`Show cancelled: ${reason}`}
                            >
                              <span className="font-semibold">{count}</span> {reason}
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* Stats cards — For Calling/Scheduling page only (desktop) */}
        {isCallingPage && !isMobile && (
          <div className="flex flex-col gap-3 mb-3 flex-shrink-0">
            {/* ── ROW 1: OVERALL / GENERAL ── */}
            <div className="flex flex-col gap-1.5">
              <button
                type="button"
                onClick={() => setOverallStatsOpen((v) => !v)}
                className="flex items-center gap-1 self-start text-[10px] font-bold uppercase tracking-wider text-gray-500 pl-1 hover:text-gray-700 focus:outline-none"
                aria-expanded={overallStatsOpen}
              >
                <svg className={`w-3 h-3 transition-transform ${overallStatsOpen ? "rotate-90" : ""}`} fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                Overall / General
              </button>
              {overallStatsOpen && (
              <div className="flex items-stretch gap-2">
                <span className="flex-shrink-0 bg-indigo-700 text-white text-[10px] font-bold uppercase tracking-wider px-3 rounded-lg shadow-sm flex items-center whitespace-nowrap">Call Attempts</span>
                <div className="flex gap-1.5">
                  {[
                    { label: "0", value: 0, cls: "bg-gray-100 text-gray-700" },
                    { label: "1", value: 1, cls: "bg-amber-100 text-amber-800" },
                    { label: "2+", value: 2, cls: "bg-red-100 text-red-800" },
                  ].map(({ label, value, cls }) => (
                    <button
                      key={value}
                      onClick={() => { setCallAttemptFilter((prev) => prev === value ? null : value); setMyCallStatusFilter(null); }}
                      className={`rounded-lg px-3 py-1 text-center border transition-all hover:shadow cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-400 min-w-[64px] ${cls} ${
                        callAttemptFilter === value ? "ring-2 ring-blue-600 border-blue-600 shadow-md" : ""
                      }`}
                    >
                      <p className="text-[10px] font-semibold uppercase tracking-wide">Attempt {label}</p>
                      <p className="text-base font-bold">{callAttemptCounts[value] || 0}</p>
                    </button>
                  ))}
                </div>
                <span className="flex-shrink-0 bg-teal-700 text-white text-[10px] font-bold uppercase tracking-wider px-3 rounded-lg shadow-sm flex items-center whitespace-nowrap ml-2">Call Status</span>
                <div className="flex gap-1.5 flex-wrap">
                  {[
                    { label: "Confirmed", value: "Confirmed", cls: "bg-green-100 text-green-800 border-green-300" },
                    { label: "KOR", value: "KOR", cls: "bg-blue-100 text-blue-800" },
                    { label: "CBR", value: "CBR", cls: "bg-purple-100 text-purple-800" },
                    { label: "Unattended", value: "Unattended", cls: "bg-orange-100 text-orange-800" },
                    { label: "Busy Tone", value: "Busy Tone", cls: "bg-yellow-100 text-yellow-800" },
                    { label: "Others", value: "Others", cls: "bg-pink-100 text-pink-800" },
                    { label: "Cancelled", value: "Cancelled", cls: "bg-red-100 text-red-800 border-red-300" },
                  ].map(({ label, value, cls }) => {
                    const isDisplayOnly = value === "Confirmed" || value === "Cancelled";
                    const count = value === "Confirmed" ? overallConfirmedCount
                      : value === "Cancelled" ? overallCancelledCount
                      : (callStatusCounts[value] || 0);
                    return isDisplayOnly ? (
                      <div
                        key={value}
                        className={`rounded-lg px-3 py-1 text-center border min-w-[64px] ${cls}`}
                        title={value === "Confirmed" ? "Confirmed records (moved to For Dispatch)" : "Cancelled records (moved to Visited/Called)"}
                      >
                        <p className="text-[10px] font-semibold uppercase tracking-wide">{label}</p>
                        <p className="text-base font-bold">{count}</p>
                      </div>
                    ) : (
                      <button
                        key={value}
                        onClick={() => { setCallStatusFilter((prev) => prev === value ? null : value); setMyCallStatusFilter(null); }}
                        className={`rounded-lg px-3 py-1 text-center border transition-all hover:shadow cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-400 min-w-[64px] ${cls} ${
                          callStatusFilter === value && !myCallStatusFilter ? "ring-2 ring-blue-600 border-blue-600 shadow-md" : ""
                        }`}
                      >
                        <p className="text-[10px] font-semibold uppercase tracking-wide">{label}</p>
                        <p className="text-base font-bold">{count}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
              )}
            </div>

            {/* ── ROW 2: INDIVIDUAL / AGENT PERFORMANCE ── */}
            {(user?.role_name === "Admin" || user?.role_name === "Call Center Agent") && (
            <div className="flex flex-col gap-1.5">
              <button
                type="button"
                onClick={() => setMyPerfStatsOpen((v) => !v)}
                className="flex items-center gap-1 self-start text-[10px] font-bold uppercase tracking-wider text-indigo-500 pl-1 hover:text-indigo-700 focus:outline-none"
                aria-expanded={myPerfStatsOpen}
              >
                <svg className={`w-3 h-3 transition-transform ${myPerfStatsOpen ? "rotate-90" : ""}`} fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                {updatedByFilter ? `${targetUserLabel}\u2019s Performance` : "My Performance"}
              </button>
              {myPerfStatsOpen && (
              <div className="flex items-stretch gap-2">
                <span className="flex-shrink-0 bg-indigo-400 text-white text-[10px] font-bold uppercase tracking-wider px-3 rounded-lg shadow-sm flex items-center whitespace-nowrap">Call Attempts</span>
                <div className="flex gap-1.5">
                  {[
                    { label: "0", value: 0, cls: "bg-gray-50 text-gray-600 border-gray-200" },
                    { label: "1", value: 1, cls: "bg-amber-50 text-amber-700 border-amber-200" },
                    { label: "2+", value: 2, cls: "bg-red-50 text-red-700 border-red-200" },
                  ].map(({ label, value, cls }) => {
                    const isActive = callAttemptFilter === value && updatedByFilter === targetUserLabel;
                    return (
                      <button
                        key={value}
                        onClick={() => {
                          if (isActive) {
                            setCallAttemptFilter(null);
                            setUpdatedByFilter("");
                          } else {
                            setCallAttemptFilter(value);
                            setCallStatusFilter(null);
                            setMyCallStatusFilter(null);
                            setUpdatedByFilter(targetUserLabel);
                          }
                          setSelected(new Set());
                        }}
                        className={`rounded-lg px-3 py-1 text-center border transition-all hover:shadow cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-400 min-w-[64px] ${cls} ${
                          isActive ? "ring-2 ring-blue-600 border-blue-600 shadow-md" : ""
                        }`}
                      >
                        <p className="text-[10px] font-semibold uppercase tracking-wide">Attempt {label}</p>
                        <p className="text-base font-bold">{userCallAttemptCounts[value] || 0}</p>
                      </button>
                    );
                  })}
                </div>
                <span className="flex-shrink-0 bg-teal-400 text-white text-[10px] font-bold uppercase tracking-wider px-3 rounded-lg shadow-sm flex items-center whitespace-nowrap ml-2">Call Status</span>
                <div className="flex gap-1.5 flex-wrap">
                  {[
                    { label: "Confirmed", value: "Confirmed", cls: "bg-green-50 text-green-700 border-green-200" },
                    { label: "KOR", value: "KOR", cls: "bg-blue-50 text-blue-700 border-blue-200" },
                    { label: "CBR", value: "CBR", cls: "bg-purple-50 text-purple-700 border-purple-200" },
                    { label: "Unattended", value: "Unattended", cls: "bg-orange-50 text-orange-700 border-orange-200" },
                    { label: "Busy Tone", value: "Busy Tone", cls: "bg-yellow-50 text-yellow-700 border-yellow-200" },
                    { label: "Others", value: "Others", cls: "bg-pink-50 text-pink-700 border-pink-200" },
                    { label: "Cancelled", value: "Cancelled", cls: "bg-red-50 text-red-700 border-red-200" },
                  ].map(({ label, value, cls }) => {
                    const isDisplayOnly = value === "Confirmed" || value === "Cancelled";
                    const count = value === "Confirmed" ? userConfirmedCount
                      : value === "Cancelled" ? userCancelledCount
                      : (userCallStatusCounts[value] || 0);
                    const isActive = !isDisplayOnly && myCallStatusFilter === value;
                    return isDisplayOnly ? (
                      <div
                        key={value}
                        className={`rounded-lg px-3 py-1 text-center border min-w-[64px] ${cls}`}
                      >
                        <p className="text-[10px] font-semibold uppercase tracking-wide">{label}</p>
                        <p className="text-base font-bold">{count}</p>
                      </div>
                    ) : (
                      <button
                        key={value}
                        onClick={() => {
                          if (isActive) {
                            setMyCallStatusFilter(null);
                          } else {
                            setMyCallStatusFilter(value);
                            setCallStatusFilter(null);
                            setCallAttemptFilter(null);
                            setUpdatedByFilter("");
                          }
                          setSelected(new Set());
                        }}
                        className={`rounded-lg px-3 py-1 text-center border transition-all hover:shadow cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-400 min-w-[64px] ${cls} ${
                          isActive ? "ring-2 ring-blue-600 border-blue-600 shadow-md" : ""
                        }`}
                      >
                        <p className="text-[10px] font-semibold uppercase tracking-wide">{label}</p>
                        <p className="text-base font-bold">{count}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
              )}
            </div>
            )}
          </div>
        )}

        <div className={`flex flex-wrap items-center justify-between gap-3 flex-shrink-0 px-3 md:px-0 ${isMobile && isDispatchPage ? '' : 'mb-3'}`}>
          {!(isMobile && isDispatchPage) && !isDispatchPage && (
            <h1 className="text-xl font-semibold text-gray-800">{title}</h1>
          )}
          <div className="flex flex-wrap items-center gap-2">
            {/* Tag buttons (Uploaded & Calling pages) */}
            {canTag && canBulkMove && selected.size > 0 && (
              (isCallingPage ? CALLING_TAG_TARGETS : TAG_TARGETS).map((t) => (
                <button
                  key={t.stage}
                  onClick={() => handleBulkTag(t.stage)}
                  className={`text-white text-sm px-3 py-2 rounded ${t.color}`}
                >
                  Move to {t.label} ({selected.size})
                </button>
              ))
            )}
            {/* Template + Import buttons (only on Uploaded page) */}
            {canImport && isUploadedPage && (
              <>
                <button
                  onClick={async () => {
                    try {
                      const response = await api.get("/migration/template", { responseType: "blob" });
                      const url = window.URL.createObjectURL(new Blob([response.data]));
                      const link = document.createElement("a");
                      link.href = url;
                      link.setAttribute("download", "migration_template.xlsx");
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      window.URL.revokeObjectURL(url);
                    } catch {
                      setActionMsg("Failed to download template.");
                    }
                  }}
                  className="bg-white text-blue-600 text-sm px-4 py-2 rounded border border-blue-600 hover:bg-blue-50"
                >
                  Download Template
                </button>
                <button
                  onClick={() => setShowImport(true)}
                  className="bg-blue-600 text-white text-sm px-4 py-2 rounded hover:bg-blue-700"
                >
                  Import Migration
                </button>
              </>
            )}
          </div>
        </div>

        {actionMsg && (
          <div className="bg-blue-50 border border-blue-200 text-blue-700 text-sm rounded px-4 py-3 mb-3 flex-shrink-0">
            {actionMsg}
            <button onClick={() => setActionMsg("")} className="ml-2 text-blue-500 hover:text-blue-800">×</button>
          </div>
        )}

        {loading ? (
          <div className="flex-1 flex items-center justify-center text-gray-500">Loading...</div>
        ) : isMobile && isDispatchPage ? (
          mobileViewMode === "map" ? (
            /* Mobile Dispatch Map View */
            <MigrationMobileMap
              dateFrom={dateFilter.from}
              onDateChange={(d) => handleDateChange({ from: d, to: d })}
              onSwitchToPage={() => { setMobileViewMode("page"); setPendingMobileLocate(null); setPendingMobileNapFocus(null); }}
              pendingLocate={pendingMobileLocate}
              pendingNapFocus={pendingMobileNapFocus}
            />
          ) : (
          /* Mobile Dispatch: single scroll container; search is sticky, everything else scrolls */
          <div className="flex-1 overflow-y-auto overflow-x-hidden" style={{ scrollBehavior: "smooth" }}>
            {/* Migration badge + date — scrolls away */}
            <div className="flex gap-2 w-full items-center px-3 pb-2">
              <span className={`flex-shrink-0 ${MIG.badgeMobile} text-xs font-bold uppercase tracking-wider px-3 py-2 rounded-lg shadow-sm`}>Migration</span>
              <button
                type="button"
                onClick={() => {
                  const d = new Date(dateFilter.from);
                  d.setDate(d.getDate() - 1);
                  const prev = d.toISOString().slice(0, 10);
                  handleDateChange({ from: prev, to: prev });
                }}
                className={`flex-shrink-0 text-sm text-white ${MIG.btnPrimaryStatic} w-8 py-2 rounded font-bold text-center`}
                title="Previous day"
              >&lt;</button>
              <input
                type="date"
                value={dateFilter.from || ""}
                onChange={(e) => { const v = e.target.value || null; handleDateChange({ from: v, to: v }); }}
                className="flex-1 border rounded-lg px-2 py-2 text-sm bg-white min-w-0"
              />
              <button
                type="button"
                onClick={() => {
                  const d = new Date(dateFilter.from);
                  d.setDate(d.getDate() + 1);
                  const next = d.toISOString().slice(0, 10);
                  handleDateChange({ from: next, to: next });
                }}
                className={`flex-shrink-0 text-sm text-white ${MIG.btnPrimaryStatic} w-8 py-2 rounded font-bold text-center`}
                title="Next day"
              >&gt;</button>
              <div className="flex flex-shrink-0 ml-1">
                <button type="button" onClick={() => setMobileViewMode("map")} className="px-3 py-2 text-xs font-semibold rounded-l-lg border bg-white text-gray-600">Map</button>
                <button type="button" className={`px-3 py-2 text-xs font-semibold rounded-r-lg border ${MIG.btnPrimaryStatic}`}>Page</button>
              </div>
            </div>
            {/* Stats cards — scrolls away */}
            <div className="grid grid-cols-4 gap-1.5 px-3 pb-2">
              {[
                { label: "Pending", countKey: "Pending", filter: "Pending", cls: "bg-yellow-50 text-yellow-800 border-yellow-200" },
                { label: "Active", countKey: "Active/Complete", filter: "Active/Complete", cls: "bg-green-50 text-green-800 border-green-200" },
                { label: "Resched", countKey: "Rescheduled", filter: "Rescheduled", cls: "bg-blue-50 text-blue-800 border-blue-200" },
                { label: "Cancel", countKey: "Cancelled", filter: "Cancelled", cls: "bg-red-50 text-red-800 border-red-200" },
              ].map(({ label, countKey, filter, cls }) => (
                <button
                  key={countKey}
                  className={`rounded-lg px-1 py-2 text-center border text-xs font-medium active:scale-95 transition-transform ${cls} ${
                    statusFilter === filter ? "ring-2 ring-blue-500 shadow" : ""
                  }`}
                  onClick={() => {
                    setSearch("");
                    setCancelReasonFilter(null);
                    setReschedReasonFilter(null);
                    setStatusFilter(prev => prev === filter ? null : filter);
                  }}
                >
                  <p className="text-[10px] uppercase tracking-wide">{label}</p>
                  <p className="text-base font-bold">{statusCounts[countKey] || 0}</p>
                </button>
              ))}
            </div>
            {/* Record count */}
            <div className="px-4 pb-2">
              <p className="text-xs text-gray-500">{filtered.length} record{filtered.length !== 1 ? "s" : ""}</p>
            </div>
            {/* Cards */}
            {filtered.length === 0 ? (
              <div className="text-center py-10 text-gray-400">No records found.</div>
            ) : (
              <div className="px-3 pb-4 space-y-2">
                {filtered.map((r, idx) => renderMobileCard(r, idx))}
              </div>
            )}
          </div>
          )
        ) : isMobile && isVisitedPage ? (
          /* Mobile Visited/Called: single scroll container with date + stats */
          <div className="flex-1 overflow-y-auto overflow-x-hidden" style={{ scrollBehavior: "smooth" }}>
            {/* Migration badge + date — scrolls away */}
            <div className="flex gap-2 w-full items-center px-3 pb-2">
              <span className={`flex-shrink-0 ${MIG.badgeMobile} text-xs font-bold uppercase tracking-wider px-3 py-2 rounded-lg shadow-sm`}>Migration</span>
              <button
                type="button"
                onClick={() => {
                  const d = new Date(dateFilter.from);
                  d.setDate(d.getDate() - 1);
                  const prev = d.toISOString().slice(0, 10);
                  handleDateChange({ from: prev, to: prev });
                }}
                className={`flex-shrink-0 text-sm text-white ${MIG.btnPrimaryStatic} w-8 py-2 rounded font-bold text-center`}
                title="Previous day"
              >&lt;</button>
              <input
                type="date"
                value={dateFilter.from || ""}
                onChange={(e) => { const v = e.target.value || null; handleDateChange({ from: v, to: v }); }}
                className="flex-1 border rounded-lg px-2 py-2 text-sm bg-white min-w-0"
              />
              <button
                type="button"
                onClick={() => {
                  const d = new Date(dateFilter.from);
                  d.setDate(d.getDate() + 1);
                  const next = d.toISOString().slice(0, 10);
                  handleDateChange({ from: next, to: next });
                }}
                className={`flex-shrink-0 text-sm text-white ${MIG.btnPrimaryStatic} w-8 py-2 rounded font-bold text-center`}
                title="Next day"
              >&gt;</button>
            </div>
            {/* Stats cards — scrolls away */}
            <div className="grid grid-cols-2 gap-1.5 px-3 pb-2">
              {[
                { label: "Completed", countKey: "Active/Complete", filter: "Active/Complete", cls: "bg-green-50 text-green-800 border-green-200" },
                { label: "Cancelled", countKey: "Cancelled", filter: "Cancelled", cls: "bg-red-50 text-red-800 border-red-200" },
              ].map(({ label, countKey, filter, cls }) => (
                <button
                  key={countKey}
                  className={`rounded-lg px-1 py-2 text-center border text-xs font-medium active:scale-95 transition-transform ${cls} ${
                    statusFilter === filter ? "ring-2 ring-blue-500 shadow" : ""
                  }`}
                  onClick={() => {
                    setSearch("");
                    setCancelReasonFilter(null);
                    setReschedReasonFilter(null);
                    setStatusFilter(prev => prev === filter ? null : filter);
                  }}
                >
                  <p className="text-[10px] uppercase tracking-wide">{label}</p>
                  <p className="text-base font-bold">{statusCounts[countKey] || 0}</p>
                </button>
              ))}
            </div>
            {/* Record count */}
            <div className="px-4 pb-2">
              <p className="text-xs text-gray-500">{filtered.length} record{filtered.length !== 1 ? "s" : ""}</p>
            </div>
            {/* Cards */}
            {filtered.length === 0 ? (
              <div className="text-center py-10 text-gray-400">No records found.</div>
            ) : (
              <div className="px-3 pb-4 space-y-2">
                {filtered.map((r, idx) => renderMobileCard(r, idx))}
              </div>
            )}
          </div>
        ) : isMobile ? (
          /* Mobile: Card view (non-dispatch pages) */
          <div className="flex-1 overflow-y-auto overflow-x-hidden pb-4" style={{ scrollBehavior: "smooth" }}>
            {filtered.length === 0 ? (
              <div className="text-center py-10 text-gray-400">No records found.</div>
            ) : (
              <div className="px-3 pb-4 space-y-2">
                {filtered.map((r, idx) => renderMobileCard(r, idx))}
              </div>
            )}
          </div>
        ) : (
          /* Desktop: Table view */
          <div className="flex-1 flex flex-col min-h-0">
            <div className="flex-1 flex flex-col min-h-0 border rounded-lg overflow-hidden">
              <div className="flex-1 min-h-0 overflow-x-auto" style={{ overflowY: "auto", scrollBehavior: "smooth" }}>
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 border-b sticky top-0 z-20">
                    <tr>
                      {showSelectCol && (
                        <th className="px-3 py-3 text-center bg-gray-50 sticky top-0 z-30" style={{ left: 0, width: 40, minWidth: 40, maxWidth: 40 }}>
                          <button
                            onClick={toggleSelectAll}
                            disabled={allIds.length === 0}
                            title={canBulkAssignTeam ? (allSelected ? "Deselect all" : "Select all without a team") : (allSelected ? "Deselect all" : "Select all")}
                            className={`w-5 h-5 border-2 rounded flex items-center justify-center mx-auto transition-colors text-xs disabled:opacity-40 ${
                              allSelected ? "bg-blue-600 border-blue-600 text-white" : "border-gray-400 hover:border-blue-500 text-transparent"
                            }`}
                          >
                            {allSelected ? "✓" : ""}
                          </button>
                        </th>
                      )}
                      <th
                        className="px-3 py-3 text-center font-medium text-gray-600 whitespace-nowrap bg-gray-50 sticky top-0 z-30 border-r border-gray-200"
                        style={{ left: showSelectCol ? 40 : 0, width: 48, minWidth: 48, maxWidth: 48 }}
                      >#</th>
                      {canViewChat && (
                        <th className="px-3 py-3 text-center font-medium text-gray-600 whitespace-nowrap bg-gray-50 w-14">Chat</th>
                      )}
                      {visibleColumns.map((c) => {
                        const sIdx = sortStack.findIndex((s) => s.key === c.key);
                        const sEntry = sIdx >= 0 ? sortStack[sIdx] : null;
                        return (
                          <th
                            key={c.key}
                            onClick={(e) => handleSortClick(c.key, e)}
                            className="px-3 py-3 text-left font-medium text-gray-600 whitespace-nowrap cursor-pointer select-none hover:bg-gray-100 bg-gray-50"
                          >
                            <span className="inline-flex items-center">
                              {c.label}
                              {sEntry ? (
                                <span className="text-blue-600 ml-1 text-xs">
                                  {sEntry.dir === "asc" ? "▲" : "▼"}
                                  {sortStack.length > 1 && <sup className="text-[0.6em] ml-0.5 font-bold text-blue-600">{sIdx + 1}</sup>}
                                </span>
                              ) : (
                                <span className="text-gray-300 ml-1 text-xs">⇅</span>
                              )}
                            </span>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {filtered.length === 0 ? (
                      <tr>
                        <td colSpan={visibleColumns.length + (showSelectCol ? 2 : 1) + (canViewChat ? 1 : 0)} className="text-center py-10 text-gray-400">
                          No records found.
                        </td>
                      </tr>
                    ) : (
                      filtered.map((r, idx) => {
                        return (
                          <Fragment key={r.reschedule_snapshot_id ? `rs-${r.reschedule_snapshot_id}` : r.id}>
                            <tr
                              className={`bg-white hover:bg-blue-50 transition-colors cursor-pointer ${selected.has(r.id) ? "bg-blue-50" : ""}`}
                              onClick={() => setDetailId(r.id)}
                            >
                              {showSelectCol && (
                                <td className="px-3 py-3 text-center sticky left-0 z-10 bg-inherit" style={{ left: 0, width: 40, minWidth: 40, maxWidth: 40 }} onClick={(e) => e.stopPropagation()}>
                                  {isRowSelectable(r) ? (
                                    <div
                                      onClick={() => toggleSelect(r.id)}
                                      className={`w-5 h-5 border-2 rounded flex items-center justify-center mx-auto cursor-pointer transition-colors text-xs ${
                                        selected.has(r.id) ? "bg-blue-600 border-blue-600 text-white" : "border-gray-400 hover:border-blue-500 text-transparent"
                                      }`}
                                    >
                                      {selected.has(r.id) ? "✓" : ""}
                                    </div>
                                  ) : (
                                    <div
                                      title="Already has a team — remove it first to reassign in bulk"
                                      className="w-5 h-5 border-2 border-gray-200 bg-gray-100 rounded mx-auto cursor-not-allowed"
                                    />
                                  )}
                                </td>
                              )}
                              <td
                                className="px-3 py-3 text-center text-gray-400 text-xs sticky z-10 bg-inherit border-r border-gray-200"
                                style={{ left: showSelectCol ? 40 : 0, width: 48, minWidth: 48, maxWidth: 48 }}
                              >{idx + 1}</td>
                              {canViewChat && (
                                <td className="px-3 py-3 whitespace-nowrap text-center w-14" onClick={(e) => e.stopPropagation()}>
                                  <button
                                    onClick={() => setChatRecordId(r.id)}
                                    className={`inline-flex items-center justify-center min-w-[28px] h-7 px-1.5 rounded-full text-xs font-bold transition-colors ${
                                      (r.chat_count || 0) === 0
                                        ? "bg-gray-100 text-gray-400"
                                        : r.has_unread_chat
                                          ? "bg-red-100 text-red-700 font-extrabold ring-1 ring-red-300"
                                          : "bg-green-100 text-green-700"
                                    }`}
                                    title={r.has_unread_chat ? "Unread messages" : (r.chat_count || 0) > 0 ? "All read" : "No messages"}
                                  >
                                    {r.has_unread_chat ? (r.unread_count || 0) : (r.chat_count || 0)}
                                  </button>
                                </td>
                              )}
                              {visibleColumns.map((c) => {
                                const isDate = c.key === "dispatch_date" || c.key === "endorsed_date";
                                const displayVal = isDate ? fmtDate(r[c.key]) : c.key === "call_status" ? (fmtCallStatus(r) || "—") : c.key === "call_attempt_count" ? (r[c.key] ?? 0) : c.key === "personnel" && isUploadedPage ? "—" : (r[c.key] || "—");

                                if (c.key === "team_number") {
                                  const isTerminalTN = r.jo_status === "Completed" || r.jo_status === "Cancelled" || r.jo_status === "Rescheduled";
                                  const canQuickTeam = canEditMigTeam && (!isTerminalTN || canEditMigTerminalTeam);
                                  const teamDisplay = isUploadedPage ? "—" : (r.team_number || "—");
                                  return (
                                    <td key={c.key} className="px-3 py-3 whitespace-nowrap">
                                      <span
                                        className={`px-2 py-0.5 rounded text-xs font-medium ${
                                          r.team_number
                                            ? "bg-indigo-50 text-indigo-700"
                                            : "bg-gray-100 text-gray-400"
                                        }${canQuickTeam ? " cursor-pointer hover:ring-2 hover:ring-indigo-300 hover:ring-offset-1 transition-shadow" : ""}`}
                                        onClick={canQuickTeam ? (e) => { e.stopPropagation(); handleMigTeamClick(r, e); } : undefined}
                                      >
                                        {teamDisplay}
                                      </span>
                                    </td>
                                  );
                                }

                                if (c.key === "jo_status" && r.jo_status) {
                                  const ageing = isDispatchPage ? calculatePendingAgeing({ ...r, job_date: r.dispatch_date }, getLocalToday()) : null;
                                  const isTerminalStatus = r.jo_status === "Completed" || r.jo_status === "Cancelled";
                                  const canQuickStatus = isDispatchPage && !isTerminalStatus && migStatusActions.length > 0;
                                  return (
                                    <td key={c.key} className="px-3 py-3 whitespace-nowrap">
                                      <span
                                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${getJoStatusColor(r.jo_status)}${canQuickStatus ? " cursor-pointer hover:ring-2 hover:ring-blue-300 hover:ring-offset-1 transition-shadow" : ""}`}
                                        onClick={canQuickStatus ? (e) => { e.stopPropagation(); handleMigStatusClick(r, e); } : undefined}
                                      >
                                        {r.jo_status}
                                        {ageing !== null && <sup className={`text-[0.65em] ml-0.5 font-bold ${getAgeingColor(ageing)}`}>{ageing}</sup>}
                                      </span>
                                    </td>
                                  );
                                }

                                if (c.key === "call_status") {
                                  const canQuickCS = isCallingPage && canEditMigCallStatus;
                                  const csDisplay = fmtCallStatus(r) || "—";
                                  const csBadge = r.call_status ? getCallStatusColor(r.call_status) : "bg-gray-100 text-gray-400";
                                  return (
                                    <td key={c.key} className="px-3 py-3 whitespace-nowrap">
                                      <div className="max-w-[200px] truncate">
                                        <span
                                          className={`px-2 py-0.5 rounded-full text-xs font-medium ${csBadge}${canQuickCS ? " cursor-pointer hover:ring-2 hover:ring-blue-300 hover:ring-offset-1 transition-shadow" : ""}`}
                                          onClick={canQuickCS ? (e) => { e.stopPropagation(); handleMigCSClick(r, e); } : undefined}
                                        >
                                          {csDisplay}
                                        </span>
                                      </div>
                                    </td>
                                  );
                                }

                                if (c.key === "nap_code") {
                                  return (
                                    <td key={c.key} className="px-3 py-3 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                                      <div className="min-w-[160px] max-w-[220px]">
                                        {r.nap_code ? <NapCodeMapLinks napCode={r.nap_code} /> : <span className="text-gray-400">—</span>}
                                      </div>
                                    </td>
                                  );
                                }

                                if (c.key === "name") {
                                  return (
                                    <td key={c.key} className="px-3 py-3 whitespace-nowrap text-gray-700 max-w-[260px]">
                                      <div className="flex items-center gap-1.5 min-w-0">
                                        {canAccessMigrationDashboard && r.name ? (
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              navigate("/migration/dispatch-dashboard", {
                                                state: {
                                                  dateFrom: dateFilter?.from,
                                                  dateTo: dateFilter?.to,
                                                  locateSubscriber: {
                                                    id: r.id,
                                                    customerName: r.name,
                                                    address: r.address,
                                                    municipality: r.municipalityname || r.municipality,
                                                    barangay: r.brgy || r.barangay,
                                                    oldacctno: r.oldacctno,
                                                    village: r.village,
                                                    latitude: r.latitude || null,
                                                    longitude: r.longitude || null,
                                                    locationSaved: r.location_saved || false,
                                                  },
                                                },
                                              });
                                            }}
                                            className="text-blue-600 hover:text-blue-800 hover:underline font-medium truncate text-left"
                                            title={`Locate ${r.name} on map`}
                                          >
                                            {r.name}
                                          </button>
                                        ) : (
                                          <span className="truncate" title={r.name || ""}>{r.name || "—"}</span>
                                        )}
                                        {r.is_priority && (
                                          <span
                                            title="Submitted by subscriber via public migration form"
                                            className="flex-shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-amber-100 text-amber-800 border border-amber-300"
                                          >
                                            <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.955a1 1 0 00.95.69h4.16c.969 0 1.371 1.24.588 1.81l-3.365 2.445a1 1 0 00-.364 1.118l1.286 3.955c.3.921-.755 1.688-1.54 1.118L10 14.347l-3.365 2.446c-.784.57-1.838-.197-1.539-1.118l1.286-3.955a1 1 0 00-.364-1.118L2.654 8.157c-.783-.57-.38-1.81.588-1.81h4.16a1 1 0 00.95-.69l1.286-3.955z"/></svg>
                                            Prio
                                          </span>
                                        )}
                                      </div>
                                    </td>
                                  );
                                }

                                return (
                                  <td key={c.key} className="px-3 py-3 whitespace-nowrap text-gray-700 max-w-[200px] truncate" title={String(displayVal)}>
                                    {displayVal}
                                  </td>
                                );
                              })}
                            </tr>
                          </Fragment>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Record count */}
            <div className="flex items-center pt-2 pb-1 flex-shrink-0 text-sm text-gray-600">
              <span>{filtered.length} record{filtered.length !== 1 ? "s" : ""}</span>
            </div>
          </div>
        )}
      </main>

      {showImport && (
        <MigrationImportModal
          onClose={() => setShowImport(false)}
          onRefresh={fetchRecords}
        />
      )}

      {detailId && (
        <MigrationDetailModal
          recordId={detailId}
          onClose={() => setDetailId(null)}
          onRefresh={fetchRecords}
          onNapLocate={isMobile && isDispatchPage ? (napCode) => {
            if (!napCode) return;
            setDetailId(null);
            setPendingMobileLocate(null);
            setPendingMobileNapFocus({ napCode });
            setMobileViewMode("map");
          } : null}
        />
      )}

      {chatRecordId && (
        <MigrationChatModal
          recordId={chatRecordId}
          onClose={() => setChatRecordId(null)}
          onCountUpdate={(recId, count) => {
            setRecords((prev) =>
              prev.map((r) =>
                r.id === recId
                  ? { ...r, chat_count: count, has_unread_chat: false, unread_count: 0 }
                  : r
              )
            );
          }}
          notifRef={migrationNotifRef}
        />
      )}

      {/* Dispatch-date prompt for bulk moves to For Dispatch / Visited/Called */}
      {bulkDateModal && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-1">
              Move to {bulkDateModal.label}
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              {selected.size} record(s) selected.{bulkDateModal.stage === "visited_called" && !isCallingPage ? " Please enter a dispatch date and select a JO Status." : isCallingPage && bulkDateModal.stage === "visited_called" ? " JO Status will be auto-set to Cancelled." : " Please enter a dispatch date."}
            </p>
            {bulkDateError && (
              <p className="text-sm text-red-600 mb-3">{bulkDateError}</p>
            )}
            <label className="block text-xs font-medium text-gray-500 mb-1">Dispatch Date</label>
            <input
              type="date"
              value={bulkDate}
              onChange={(e) => { setBulkDate(e.target.value); setBulkDateError(""); }}
              className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 mb-4"
            />
            {bulkDateModal.stage === "visited_called" && !isCallingPage && (
              <>
                <label className="block text-xs font-medium text-gray-500 mb-1">JO Status <span className="text-red-500">*</span></label>
                <select
                  value={bulkJoStatus}
                  onChange={(e) => { setBulkJoStatus(e.target.value); setBulkDateError(""); }}
                  className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 mb-4"
                >
                  <option value="">— Select JO Status —</option>
                  <option value="Completed">Completed</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </>
            )}
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setBulkDateModal(null)}
                className="px-4 py-2 text-sm rounded border text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkDateConfirm}
                className="px-4 py-2 text-sm rounded bg-blue-600 text-white hover:bg-blue-700"
              >
                Confirm Move
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick status-action menu */}
      {statusMenu && (
        <StatusActionMenu
          anchorRect={statusMenu.rect}
          onSelect={handleMigStatusAction}
          onClose={() => setStatusMenu(null)}
          isMobile={isMobile}
          visibleActions={migStatusActions}
        />
      )}

      {/* Direct status modals from quick action */}
      {directAction?.type === "complete" && (
        <MigQuickCompleteDialog key={directAction.record.id} record={directAction.record} onDone={handleMigDirectDone} onCancel={() => setDirectAction(null)} />
      )}
      {directAction?.type === "cancel" && (
        <MigQuickCancelDialog key={directAction.record.id} record={directAction.record} onDone={handleMigDirectDone} onCancel={() => setDirectAction(null)} />
      )}
      {directAction?.type === "reschedule" && (
        <MigQuickRescheduleDialog key={directAction.record.id} record={directAction.record} onDone={handleMigDirectDone} onCancel={() => setDirectAction(null)} />
      )}

      {/* Quick call-status action menu (for_calling_scheduling only) */}
      {csMenu && (
        <CallStatusActionMenu
          options={MIG_CALL_STATUS_OPTIONS}
          anchorRect={csMenu.rect}
          onSelect={handleMigCSSelect}
          onClose={() => setCsMenu(null)}
          isMobile={isMobile}
        />
      )}

      {/* Quick call-status dialog */}
      {csAction && (
        <MigQuickCallStatusDialog
          key={csAction.record.id}
          record={csAction.record}
          selectedStatus={csAction.selectedStatus}
          onDone={handleMigCSDone}
          onCancel={() => setCsAction(null)}
        />
      )}

      {/* Inline team assignment popover */}
      {teamMenu && (
        <TeamAssignmentPopover
          key={teamMenu.record.id}
          anchorRect={teamMenu.rect}
          record={teamMenu.record}
          assignmentDate={teamMenu.record.dispatch_date}
          apiPath="/migration"
          onSave={handleMigTeamSave}
          onClose={() => setTeamMenu(null)}
        />
      )}

      {/* Bulk team assignment modal (For Dispatch page) */}
      {showBulkTeamModal && bulkAssignIds.length > 0 && (
        <BulkTeamAssignModal
          recordIds={bulkAssignIds}
          assignmentDate={
            records.find((r) => bulkAssignIds.includes(r.id))?.dispatch_date
            || dateFilter?.from
            || getLocalToday()
          }
          onDone={handleBulkTeamDone}
          onMerge={mergeUpdatedRecords}
          onClose={() => setShowBulkTeamModal(false)}
        />
      )}
    </div>
  );
}
